import {EventEmitter, Injectable} from '@angular/core';
import {
  Container,
  Cursor,
  Drawer,
  DrawForeignObject,
  DrawImage,
  DrawTool,
  DrawVideo,
  EditNodeTool,
  EditTableTool,
  ElementType,
  Focus,
  ForeignObjectView,
  FreeView,
  GroupView,
  HighlightTool,
  ImageView,
  Path,
  PathView, Point,
  PointedView,
  PointerTool,
  Rect,
  ResizeMode,
  SelectTool,
  Size,
  Table,
  TableView,
  TextBoxView,
  VideoView
} from '@app/modules/svg/public-api';
import {LocalUser, LocalUserState} from '@app/state/local-user.state';
import {document} from 'ngx-bootstrap/utils';
import {
  ClassroomMessage,
  DataService,
  MessageRecord,
  MsgRecordType,
  RecordArgs,
  RecordStack,
  StateValue
} from '@app/services/data.service';
import {AccessType} from '@app/modules/shared/data/constants';
import {ElementView} from '@app/modules/svg/element/ElementView';
import {Store} from '@ngxs/store';
import {LocalStack, Whiteboard, WhiteboardAction, WhiteboardState} from '@app/state/whiteboard.state';
import {WebSocket, WebSocketService} from '@app/modules/shared/services/web-socket.service';
import {ClassroomState, ClassroomStateModel} from '@app/state/classroom-state.service';
import {MessageHandler} from '@app/services/message-handler';
import {
  RemoteUserAdditionalInfoStateModel,
  RemoteUserInfoStateModel,
  RemoteUsersInfoState
} from '@app/state/remote-users.state';
import {WhiteboardCommands, WhiteboardEvents, WhiteboardStates} from '@app/models/commands/whiteboard-commands';
import {HttpClient, HttpEvent, HttpHeaders, HttpRequest, HttpResponse} from '@angular/common/http';
import {ModuleService} from '@app/services/module-service';
import {NumberLineView2} from '@app/modules/svg/element/complex/cartesian2/NumberLineView2';
import {CoordinatePlaneView2} from '@app/modules/svg/element/complex/cartesian2/CoordinatePlaneView2';
import {AssetHTML, AssetURL, ProblemHTML, ProblemURL} from '@app/models/webportal/Asset';
import {Observable, ObservableInput} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {UtilService} from '@app/modules/shared/services/util.service';
import {DrawCircle} from '@app/modules/svg/service/tool/draw/element/shape/circular/DrawCircle';
import {DrawEllipse} from '@app/modules/svg/service/tool/draw/element/shape/circular/DrawEllipse';
import domtoimage from 'dom-to-image';
import {S3Url} from '@app/models/s3-url';
import {ProblemPreprocessor} from '@app/modules/whiteboard/services/problem-preprocessor';
import {GraphicView} from '@app/modules/svg/element/complex/cartesian/GraphicView';
import {EditSegmentTool} from '@app/modules/svg/service/tool/edit/cartesian/EditSegmentTool';
import {SegmentView} from '@app/modules/svg/element/complex/cartesian/graphic/SegmentView';

@Injectable({
  providedIn: 'root'
})
export class WhiteboardService implements MessageHandler, ModuleService {
  protected static readonly PROBLEM_LEFT_MARGIN: number = 10;
  protected static readonly PROBLEM_TOP_MARGIN: number = 10;
  protected static readonly SUB_PROBLEM_TOP_MARGIN: number = 5;

  protected dataService: DataService;

  protected _whiteboard: Whiteboard;
  protected classroom: ClassroomStateModel;
  protected localUser: LocalUser;
  protected remoteUsers: { [key: number]: RemoteUserAdditionalInfoStateModel } = {};

  public isInRestrictedMode: boolean = false;
  public readonly extendEventEmitter: EventEmitter<number> = new EventEmitter<number>();

  protected readonly elementStringTypeKey: string = 'typeLog';

  public handleRecord: (record: MessageRecord, nonQueue: boolean) => void | Promise<void>; /* should be set in WhiteboardCommands */
  public handleRecordStack: (recordStack: RecordStack) => Promise<void>; /* should be set in WhiteboardCommands */

  public onMessageSendError: EventEmitter<{status: WebSocket.MessageSendStatus; messages: {[key: string]: string}}> = new EventEmitter();
  public onInit: EventEmitter<Whiteboard> = new EventEmitter();

  protected readonly teacherBroadcast: string = '/app/whiteboard/t';
  protected readonly teacherPrivate: string = '/app/whiteboard/tp';
  protected readonly publicBroadcast: string = '/app/whiteboard/b';
  protected readonly undoDestination: string = `/app/whiteboard/undo`;
  protected readonly redoDestination: string = `/app/whiteboard/redo`;
  protected readonly purgeDestination: string = `/app/whiteboard/purge`;

  protected readonly addDestination: string = `/app/whiteboard/add`;
  protected readonly subDestination: string = `/app/whiteboard/sub`;
  protected readonly delDestination: string = `/app/whiteboard/del`;

  protected readonly getStatePath: string = '/whiteboard/get-state';

  /* elements whose width comes from their container or from their table rather than from
     their own content, so it says nothing about how wide the content really is */
  private static readonly DERIVED_WIDTH_DISPLAYS: string[] = [
    'block', 'flex', 'grid', 'list-item', 'flow-root',
    'table-row', 'table-row-group', 'table-header-group', 'table-footer-group',
    'table-cell', 'table-column', 'table-column-group', 'table-caption'
  ];

  /* the cell CMS puts the problem body into - identifies its layout table wrapper */
  private static readonly PROBLEM_CELL_SELECTOR: string = 'td[class^="xsl-problem-text"]';
  private static readonly STRETCHED_ATTRIBUTE: string = 'data-vcr-stretched';

  private static stretchProblemWrapper(div: HTMLDivElement): void {
    div.querySelectorAll('table').forEach((table: HTMLTableElement): void => {
      if (!table.querySelector(WhiteboardService.PROBLEM_CELL_SELECTOR)) {
        return;
      }

      if (table.style.width || table.getAttribute('width')) {
        return;
      }

      table.style.width = '100%';
      table.setAttribute(WhiteboardService.STRETCHED_ATTRIBUTE, '');
    });
  }

  constructor(
    protected webSocketService: WebSocketService,
    protected utilService: UtilService,
    protected http: HttpClient,
    protected store: Store
  ) {
    // !!! LAZY INIT OBJECTS (must NOT be used in constructor):
    // - this.dataService

    this.listenStoreChanges();
    this.listenWhiteboardStateChanges();
  }

  public afterShare: () => void = () => {};
  public afterShareWith: (userId: number) => void = () => {};
  public afterUnshare: () => void = () => {};
  public afterUnshareWith: (userId: number) => void = () => {};

  public setDataService(dataService: DataService): void {
    this.dataService = dataService;
  }

  protected listenStoreChanges(): void {
    this.store.select(ClassroomState).subscribe((classroom: ClassroomStateModel) => {
      this.classroom = classroom;
    });

    this.store.select(LocalUserState).subscribe((localUser: LocalUser) => {
      this.localUser = localUser;
    });

    this.store.select(RemoteUsersInfoState).subscribe(({usersInfoById}: RemoteUserInfoStateModel) => {
      this.remoteUsers = usersInfoById;
    });
  }
  protected listenWhiteboardStateChanges(): void {
    this.store.select(WhiteboardState).subscribe((whiteboard: Whiteboard) => {
      this._whiteboard = whiteboard;
    });
  }

  protected dispatchContainerState(container: Container): void {
    this.store.dispatch(new WhiteboardAction.SetContainer(container));
  }

  protected dispatchWrapper(wrapper: HTMLDivElement): void {
    this.store.dispatch(new WhiteboardAction.SetWrapper(wrapper));
  }

  protected dispatchStaffPointerTool(staffPointerTool: PointerTool): void {
    this.store.dispatch(new WhiteboardAction.SetStaffPointerTool(staffPointerTool));
  }

  protected dispatchStaffPointerCount(count: number): void {
    this.store.dispatch(new WhiteboardAction.SetStaffPointersCount(count));
  }

  protected dispatchBroadcastScroll(broadcast: boolean): void {
    this.store.dispatch(new WhiteboardAction.SetBroadcastScroll(broadcast));
  }

  protected dispatchStreamingState(streaming: boolean): void {
    this.store.dispatch(new WhiteboardAction.SetStreaming(streaming));
  }

  protected dispatchSmoothState(smooth: boolean): void {
    this.store.dispatch(new WhiteboardAction.SetSmooth(smooth));
  }

  protected dispatchHeight(height: number): void {
    this.store.dispatch(new WhiteboardAction.SetHeight(height));
  }

  protected dispatchWidth(width: number): void {
    this.store.dispatch(new WhiteboardAction.SetWidth(width));
  }

  protected dispatchScrollPosition(position: number): void {
    this.store.dispatch(new WhiteboardAction.SetScrollPosition(position));
  }

  protected dispatchLastPostedProblemId(id: number): void {
    this.store.dispatch(new WhiteboardAction.SetLastPostedProblemId(id));
  }

  protected dispatchShareWithAll(): void {
    this.store.dispatch(new WhiteboardAction.ShareWithAll());
  }

  protected dispatchShareWith(id: number): void {
    this.store.dispatch(new WhiteboardAction.ShareWith(id));
  }

  protected dispatchUnshareWithAll(): void {
    this.store.dispatch(new WhiteboardAction.UnshareWithAll());
  }

  protected dispatchUnshareWith(id: number): void {
    this.store.dispatch(new WhiteboardAction.UnshareWith(id));
  }

  protected dispatchReset(): void {
    this.store.dispatch(new WhiteboardAction.Reset());
  }

  public init(whiteboardId: string, wrapperId: string, idPrefix: string): void {
    this.dispatchReset();
    /* throws DOMException when svg element with id 'whiteboardId' not found */
    try {
      const container: Container = new Container(whiteboardId, this.localUser.id + '', idPrefix, Date.now());
      /* share page clipboard with WB */
      container.focused.clipboard = this.utilService.clipboard;
      container.focused.safeClipboard = true;
      container.style.cursor[Cursor.DRAW_FREE] = 'url(\'assets/img/whiteboard/pencil-cursor.svg\') 0 20, auto';
      container.style.cursor[Cursor.ERASER] = 'url(\'assets/img/whiteboard/eraser.svg\') 8 24, auto';

      this.dispatchContainerState(container);
    } catch (exception) {
      throw new DOMException(`Can't find whiteboard container by id '${whiteboardId}'`, 'Container not found');
    }

    const wrapper: HTMLDivElement = document.getElementById(wrapperId);
    wrapper.style.height = WhiteboardState.VISIBLE_HEIGHT + 'px';

    if (!wrapper) {
      throw new DOMException(`Can't find whiteboard wrapper by id '${wrapperId}'`, 'Wrapper not found');
    }

    this._whiteboard.container.focused.safeClipboard = true;

    this.dispatchWrapper(wrapper);
    this.dispatchStaffPointerTool(this.createStaffPointer());

    window.addEventListener('beforeunload', () => {
      this._whiteboard.container.tools.activeTool?.off();
    });

    this.onScrollStopped(() => {
      if (this._whiteboard.broadcastScroll) {
        this.sendScroll(this._whiteboard.wrapper.scrollTop);
      }
      this.dispatchBroadcastScroll(true);
    });

    this.onInit.emit(this._whiteboard);
  }

  public getState(): Observable<any> {
    this._whiteboard.localStack.clear();

    const turnOffActiveTool: boolean = this.localUser.accessType === AccessType.STUDENT || this.localUser.accessType === AccessType.OBSERVER || this.isInRestrictedMode;
    this.reset(false, turnOffActiveTool);

    if (this.localUser.accessType === AccessType.STUDENT) {
      this.whiteboard.container.style.strokeColor = this.localUser.personalColor;
      this.whiteboard.container.style.fontColor = this.localUser.personalColor;
    }

    return this.http.get(`${this.classroom.vcrURL}${this.getStatePath}`)
      .pipe(map((result: any) => {
        console.log(`GET STATE - ${result.message}`);
        this._whiteboard.wrapper.style.scrollBehavior = 'unset';
        this.handleRecordStack(result.data as RecordStack).then(() => {
          this._whiteboard.wrapper.style.scrollBehavior = 'smooth';
        });
      }));
  }

  public get whiteboard(): Whiteboard {
    return this._whiteboard;
  }

  public get localStack(): LocalStack {
    return this._whiteboard.localStack;
  }

  public get height(): number {
    return this._whiteboard.height;
  }
  public get wrapper(): HTMLDivElement {
    return this._whiteboard.wrapper;
  }

  public setStreaming(isOn: boolean, broadcast: boolean = true): void {
    this.dispatchStreamingState(isOn);

    if (broadcast) {
      if (this._whiteboard.streaming) {
        this.sendStreamingOn();
      } else {
        this.sendStreamingOff();
      }
    }
  }
  public setSmooth(isOn: boolean, broadcast: boolean = true): void {
    this._whiteboard.container.tools.drawTool.smooth.isOn = isOn;
    this.dispatchSmoothState(isOn);

    if (broadcast) {
      if (this._whiteboard.smooth) {
        this.sendSmoothOn();
      } else {
        this.sendSmoothOff();
      }
    }
  }

  public cannotBeExtended(): boolean {
    return this._whiteboard.height + WhiteboardState.VISIBLE_HEIGHT > WhiteboardState.MAX_HEIGHT;
  }

  /** if extend height is not given, whiteboard will be extended by this.VISIBLE_HEIGHT, otherwise by extendHeight */
  public extend(broadcast: boolean = true, extendHeight: number = undefined): void {
    if (extendHeight) {
      if (extendHeight > WhiteboardState.MAX_HEIGHT) {
        return;
      }
      this.dispatchHeight(extendHeight);
    } else {
      if (this.cannotBeExtended()) {
        return;
      }
      this.dispatchHeight(this._whiteboard.height + WhiteboardState.VISIBLE_HEIGHT);
    }
    this._whiteboard.container.HTML.style.height = this._whiteboard.height + 'px';

    this.dispatchBroadcastScroll(false);
    this._whiteboard.wrapper.scrollTop = this._whiteboard.height;
    this.dispatchScrollPosition(this._whiteboard.height);
    if (this._whiteboard.container.grid.isGridOn()) {
      this._whiteboard.container.grid.gridOff();
      this._whiteboard.container.grid.gridOn();
    }

    this.extendEventEmitter.emit(this._whiteboard.height);
    if (broadcast) {
      this.sendHeight(this._whiteboard.height);
    }
  }
  public updateScroll(): void {
    this._whiteboard.wrapper.style.scrollBehavior = 'unset';

    const broadcastScrollOld: boolean = this._whiteboard.broadcastScroll;
    this.scroll(this._whiteboard.scrollPosition, false);
    this.dispatchBroadcastScroll(broadcastScrollOld);

    this._whiteboard.wrapper.style.scrollBehavior = 'smooth';
  }
  /** scroll whiteboard to scroll position and save that position */
  public onScrollStopped(callback: () => void, timeout: number = 50): void {
    let isScrolling: NodeJS.Timer;
    this._whiteboard.wrapper.addEventListener('scroll', () => {
      this.dispatchScrollPosition(this._whiteboard.wrapper.scrollTop);
      clearTimeout(isScrolling);
      isScrolling = setTimeout(callback, timeout);
    }, false);
  }
  public scroll(scroll: number, send: boolean = true): void {
    this.dispatchBroadcastScroll(send);
    this._whiteboard.wrapper.scrollTop = scroll;
    this.dispatchScrollPosition(scroll);
  }
  public reset(broadcast: boolean = true, turnOffActiveTool: boolean = false): void {
    if (this._whiteboard.container.tools.drawTool.isOn()) {
      this._whiteboard.container.tools.drawTool.abortDrawing();
    }
    this._whiteboard.container.reset();
    this._whiteboard.localStack.events = [];
    this._whiteboard.localStack.pointer = 0;

    this.dispatchHeight(WhiteboardState.VISIBLE_HEIGHT);
    this.extendEventEmitter.emit(this._whiteboard.height);
    for (const studentPointer of Object.values(this._whiteboard.tools.studentPointerTools)) {
      studentPointer?.off();
    }

    this.dispatchBroadcastScroll(false);
    this._whiteboard.container.HTML.style.height = '';
    this._whiteboard.container.grid.gridOff();
    this._whiteboard.container.grid.snapOff();
    this._whiteboard.container.style.resetToDefault();

    this.dispatchLastPostedProblemId(null); // Reset header
    this.setSmooth(true, false);
    this.setStreaming(false, false);

    if (turnOffActiveTool) {
      this._whiteboard.container.tools.activeTool?.off();
    } else {
      this._whiteboard.container.tools.drawTool.setDrawer(this._whiteboard.container.drawers.free);
      this._whiteboard.container.tools.drawTool.on();
    }
    if (broadcast) {
      this.sendReset();
    }
  }

  public updateGrid(): void {
    if (this._whiteboard.container.grid.isGridOn()) {
      this._whiteboard.container.grid.gridOff(false);
      this._whiteboard.container.grid.gridOn(false);
    }
  }

  public onLogout(): void {
    this._whiteboard.container.tools.activeTool?.off();
  }

  public updateLocalStack(events: MessageRecord[], pointer: number): void {
    this._whiteboard.localStack.events = events;
    this._whiteboard.localStack.pointer = pointer;
  }
  public redraw(): void {
    this._whiteboard.container.clear();
    this._whiteboard.localStack.events.slice(0, this._whiteboard.localStack.pointer).forEach((event: MessageRecord) =>  {
      this.handleRecord(event, true);
    });
  }
  public clearEmptyTextBoxes(): void {
    this._whiteboard.container.elements.forEach((element) => {
      if (element instanceof TextBoxView && element.text === '') {
        this._whiteboard.container.remove(element, true, false);
      }
    });
  }

  protected showStaffPointer(): void {
    this._whiteboard.staffPointerTool.add();
  }
  protected hideStaffPointer(): void {
    this._whiteboard.staffPointerTool.remove();
  }
  public setStaffPointerCount(count: number): void {
    this.dispatchStaffPointerCount(count);
    if (count <= 0) {
      this.hideStaffPointer();
      this.dispatchStaffPointerCount(0);
    } else {
      this.showStaffPointer();
    }
  }
  public incrementStaffPointerCount(): void {
    this.dispatchStaffPointerCount(this._whiteboard.staffPointersCount + 1);
  }
  public decrementStaffPointerCount(): void {
    this.dispatchStaffPointerCount(this._whiteboard.staffPointersCount - 1);
    if (this._whiteboard.staffPointersCount <= 0) {
      this.hideStaffPointer();
      this.dispatchStaffPointerCount(0);
    } else {
      this.showStaffPointer();
    }
  }
  public getStaffPointerTool(): PointerTool {
    return this._whiteboard.staffPointerTool;
  }
  public createStaffPointer(): PointerTool {
    const path = new Path();
    path.fromString('M 19.391119,33.920086 H 33.429924 L 1.75,1.749748 V 46.174501 L 12.027344,36.980721 L 19.526756,54.429783 L 26.895341,51.292488 Z');
    const pointerTool = new PointerTool(this._whiteboard.container, path);
    pointerTool.setStrokeWidth('3.5');
    pointerTool.setStrokeColor('#0da83999');
    pointerTool.setFillColor('#7efca099');
    return pointerTool;
  }
  public createStudentPointer(color: string): PointerTool {
    const path = new Path();
    path.fromString('M 13.014693,23.152718 H 22.575988 L 1,0.99985408 V 31.591249 L 7.9995071,25.260306 L 13.107071,37.275931 L 18.125533,35.115554 Z');
    const pointerTool = new PointerTool(this._whiteboard.container, path);
    pointerTool.setStrokeWidth('2');
    pointerTool.setStrokeColor(color + '99');
    pointerTool.setFillColor(color + '99');
    return pointerTool;
  }

  /* event handling */
  public sendShare(): WebSocket.MessageSendStatus {
    return this.send(this.teacherBroadcast, {
      store: true,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        WhiteboardStates.SHARE,
        new RecordArgs('[]')
      )
    });
  }
  public onShare(record: MessageRecord): void {
    const sharedUsersIds: number[] = JSON.parse(record.data);
    if (sharedUsersIds.length === 0) {
      this.shareWithAll();
    } else {
      sharedUsersIds.forEach((id: number) => {
        this.shareWith(id);
      });
    }
  }

  public sendShareWith(userId: number): WebSocket.MessageSendStatus {
    const recordData: string = JSON.stringify([userId]);
    return this.send(this.addDestination, {
      store: false,
      broadcast: true,
      selfProcess: true,
      mirrorState: WhiteboardStates.SHARE,
      record: this.buildRecord(
        WhiteboardStates.SHARE_WITH,
        new RecordArgs(recordData)
      )
    });
  }
  public onShareWith(record: MessageRecord): void {
    const sharedUsersIds: number[] = JSON.parse(record.data);
    sharedUsersIds.forEach((id: number) => {
      this.shareWith(id);
    });
  }

  public sendUnshare(): WebSocket.MessageSendStatus {
    return this.send(this.delDestination, {
      store: false,
      broadcast: true,
      selfProcess: true,
      mirrorState: WhiteboardStates.SHARE,
      record: this.buildRecord(
        WhiteboardStates.UNSHARE,
        new RecordArgs('')
      )
    });
  }
  public onUnshare(): void {
    this.unshareWithAll();
  }

  public sendUnshareWith(userId: number): WebSocket.MessageSendStatus {
    const recordData: string = JSON.stringify([userId]);
    if (this.whiteboard.sharedUsers.size === 1) {
      return this.send(this.delDestination, {
        store: false,
        broadcast: true,
        selfProcess: true,
        mirrorState: WhiteboardStates.SHARE,
        record: this.buildRecord(
          WhiteboardStates.UNSHARE_WITH,
          new RecordArgs(recordData)
        )
      });
    } else {
      return this.send(this.subDestination, {
        store: false,
        broadcast: true,
        selfProcess: true,
        mirrorState: WhiteboardStates.SHARE,
        record: this.buildRecord(
          WhiteboardStates.UNSHARE_WITH,
          new RecordArgs(recordData)
        )
      });
    }
  }
  public onUnshareWith(record: MessageRecord): void {
    const unsharedUsersIds: number[] = JSON.parse(record.data);
    unsharedUsersIds.forEach((id: number) => {
      this.unshareWith(id);
    });
  }

  public sendUndo(): WebSocket.MessageSendStatus {
    this._whiteboard.container.tools.drawTool.stopDrawing();
    /** Blur container if Select tool is on before undoing to prevent freezing Student's WB after undoing */
    if (this._whiteboard.container.tools.selectTool.isOn()) {
      this._whiteboard.container.blur();
    }
    return this.send(this.undoDestination, {
      store: false,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        WhiteboardEvents.UNDO,
        new RecordArgs('')
      )
    });
  }
  public onUndo(): void {
    /* fix current editable element id if exists */
    const editableElementId: {ownerId: string; index: number} = this.getEditableElementId();

    this._whiteboard.localStack.undoPointer();
    this.redraw();

    /* Handle a problem-header case */
    const undoneEventData: any = JSON.parse(this.localStack.events[this.localStack.pointer].data);
    if (undoneEventData.isHeader) {
      this.dispatchLastPostedProblemId(null); // Reset header
    }

    if (editableElementId) {
      this.setEditableElement(editableElementId);
    }

    this.clearEmptyTextBoxes();
  }

  public sendRedo(): WebSocket.MessageSendStatus {
    return this.send(this.redoDestination, {
      store: false,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        WhiteboardEvents.REDO,
        new RecordArgs('')
      )
    });
  }
  public onRedo(): void {
    /* fix current editable element id if exists */
    const editableElementId: {ownerId: string; index: number} = this.getEditableElementId();

    this._whiteboard.localStack.redoPointer();
    this.redraw();

    /* Handle a problem-header case */
    const redoneEventData: any = JSON.parse(this.localStack.events[this.localStack.pointer - 1].data);
    if (redoneEventData.isHeader) {
      this.dispatchLastPostedProblemId(redoneEventData.id);
    }

    if (editableElementId) {
      this.setEditableElement(editableElementId);
    }

    this.clearEmptyTextBoxes();
  }

  public refresh(): void {
    /** Blur container if Select tool is on before refreshing to prevent freezing Student's WB after refreshing */
    if (this._whiteboard.container.tools.selectTool.isOn()) {
      this._whiteboard.container.blur();
    }
    this.sendRefresh();
  }

  public sendRefresh(): WebSocket.MessageSendStatus {
    return this.send(this.teacherBroadcast, {
      store: false,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        WhiteboardEvents.REFRESH,
        new RecordArgs('')
      )
    });
  }
  public onRefresh(record: MessageRecord): void {
    const editableElementId: {ownerId: string; index: number} = this.getEditableElementId();
    this.getState().subscribe(() => {
      if (editableElementId) {
        this.setEditableElement(editableElementId);
      }
    });
  }

  public sendPurge(): WebSocket.MessageSendStatus {
    return this.send(this.purgeDestination, {
      store: false,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        WhiteboardEvents.PURGE,
        new RecordArgs('')
      )
    });
  }
  public onPurge(record: MessageRecord): void {
    const turnOffActiveTool: boolean = this.localUser.accessType === AccessType.STUDENT;

    this.reset(false, turnOffActiveTool);

    if (this.localUser.accessType === AccessType.STUDENT) {
      this._whiteboard.container.style.strokeColor = this.localUser.personalColor;
      this._whiteboard.container.style.fontColor = this.localUser.personalColor;

      if (this._whiteboard.sharedUsers) {
        if (this._whiteboard.sharedUsers.size === 0) { /* shared with all */
          this._whiteboard.container.tools.drawTool.setDrawer(this._whiteboard.container.drawers.free);
          this._whiteboard.container.tools.drawTool.on();
        } else if (this._whiteboard.sharedUsers.has(this.localUser.id)) { /* shared with local user */
            if (this._whiteboard.sharedUsers.size === 1) { /* shared with only local user */
              this._whiteboard.container.tools.pointerTool.on();
              WhiteboardService.hidePointer(this._whiteboard.container.tools.pointerTool);
            } else { /* shared with not only local user */
              this._whiteboard.container.tools.drawTool.setDrawer(this._whiteboard.container.drawers.free);
              this._whiteboard.container.tools.drawTool.on();
            }
        }
      }
    }
  }


  public sendDrawMouseDown(parameters: any): WebSocket.MessageSendStatus {
    if (!this._whiteboard.streaming) {
      return;
    }

    const data = JSON.stringify({
      position: parameters.position,
      element: parameters.element.toJSON()
    });

    return this.send(this.publicBroadcast, {
      store: false,
      broadcast: true,
      record: this.buildRecord(
        WhiteboardEvents.DRAW_MOUSE_DOWN,
        new RecordArgs(data)
      )
    });
  }
  public onDrawMouseDown(record: MessageRecord): void {
    const recordData: any = JSON.parse(record.data);
    const element: ElementView = this._whiteboard.container.createElementFromJSON(recordData.element);

    if (!this._whiteboard.tools.drawTools[record.ownerId]) {
      this._whiteboard.tools.drawTools[record.ownerId] = new DrawTool(this._whiteboard.container);
      // this.drawTools[record.ownerId].mirroring = false;
      this._whiteboard.tools.drawTools[record.ownerId].setDrawer(this._whiteboard.container.drawers.getByType(recordData.element.type)._new(), false);
      const drawer: Drawer = this._whiteboard.tools.drawTools[record.ownerId].getDrawer();

      if (drawer instanceof DrawImage) {
        drawer.src = (element as ImageView).src;
      } else if (drawer instanceof DrawVideo) {
        drawer.src = (element as VideoView).src;
      } else if (drawer instanceof DrawForeignObject) {
        /* set always at the end, because DrawVideo and DrawImage are also instances of ForeignObjectView */
        drawer.content = decodeURIComponent(recordData.element.content.innerHTML);
      } else if (drawer instanceof DrawCircle || drawer instanceof DrawEllipse) {
        this._whiteboard.tools.drawTools[record.ownerId].mirroring = true;
      }
    }
    this._whiteboard.tools.drawTools[record.ownerId].makeMouseDown(recordData.position, false);

    /* drawTools[event.ownerId].drawableElement can be null, when drawing segment, on second click, drawing will be finished */
    if (this._whiteboard.tools.drawTools[record.ownerId].drawableElement) {
      this._whiteboard.tools.drawTools[record.ownerId].drawableElement.__onBlur__();
      this._whiteboard.tools.drawTools[record.ownerId].drawableElement.style.set = element.style;
      this._whiteboard.tools.drawTools[record.ownerId].drawableElement.setId(element.ownerId, element.index);
      this._whiteboard.tools.drawTools[record.ownerId].drawableElement.selectable = false;
    }
  }

  public sendDrawMouseMove(parameters: any): WebSocket.MessageSendStatus {
    if (!this._whiteboard.streaming) {
      return;
    }
    const data = JSON.stringify({
      position: parameters.position,
      additional: {
        path:
          (parameters.element instanceof PathView) ?
            parameters.element.path.toString() :
            undefined
      }
    });
    return this.send(this.publicBroadcast, {
      store: false,
      broadcast: true,
      record: this.buildRecord(
        WhiteboardEvents.DRAW_MOUSE_MOVE,
        new RecordArgs(data)
      )
    });
  }
  public onDrawMouseMove(record: MessageRecord): void {
    const recordData: any = JSON.parse(record.data);
    this._whiteboard.tools.drawTools[record.ownerId]?.makeMouseMove(recordData.position, false, recordData.additional);
  }

  public sendDrawMouseUp(parameters: any): WebSocket.MessageSendStatus {
    if (!this._whiteboard.streaming) {
      return;
    }
    const data = JSON.stringify({
      position: parameters.position,
      additional: {
        path: (parameters.element instanceof FreeView) ? parameters.element.path.toString() : undefined
      }
    });
    return this.send(this.publicBroadcast, {
      store: false,
      broadcast: true,
      record: this.buildRecord(
        WhiteboardEvents.DRAW_MOUSE_UP,
        new RecordArgs(data)
      )
    });
  }
  public onDrawMouseUp(record: MessageRecord): void {
    const recordData = JSON.parse(record.data);
    this._whiteboard.tools.drawTools[record.ownerId]?.makeMouseUp(recordData.position, false, recordData.additional);

    this.setElementSelectable(this._whiteboard.tools.drawTools[record.ownerId]?.drawableElement);

    this._whiteboard.tools.drawTools[record.ownerId] = undefined;
  }

  public sendStopClickDrawing(): WebSocket.MessageSendStatus {
    if (!this._whiteboard.streaming) {
      return;
    }
    return this.send(this.publicBroadcast, {
      store: false,
      broadcast: true,
      record: this.buildRecord(
        WhiteboardEvents.STOP_CLICK_DRAWING,
        new RecordArgs('')
      )
    });
  }
  public onStopClickDrawing(record: MessageRecord): void {
    if (!this._whiteboard.tools.drawTools[record.ownerId]) {
      return;
    }

    this.setElementSelectable(this._whiteboard.tools.drawTools[record.ownerId]?.drawableElement);

    this._whiteboard.tools.drawTools[record.ownerId].stopDrawing(false);
    this._whiteboard.tools.drawTools[record.ownerId] = undefined;
  }

  public sendPerfectModeOn(): WebSocket.MessageSendStatus {
    if (!this._whiteboard.streaming) {
      return;
    }
    return this.send(this.publicBroadcast, {
      store: false,
      broadcast: true,
      record: this.buildRecord(
        WhiteboardEvents.PERFECT_MODE_ON,
        new RecordArgs('')
      )
    });
  }
  public onPerfectModeOn(record: MessageRecord): void {
    if (this._whiteboard.tools.drawTools[record.ownerId]) {
      this._whiteboard.tools.drawTools[record.ownerId].perfect = true;
    }

    if (this._whiteboard.focuses[record.ownerId]) {
      this._whiteboard.focuses[record.ownerId].boundingBox.perfect = true;
    }
  }

  public sendPerfectModeOff(): WebSocket.MessageSendStatus {
    if (!this._whiteboard.streaming) {
      return;
    }
    return this.send(this.publicBroadcast, {
      store: false,
      broadcast: true,
      record: this.buildRecord(
        WhiteboardEvents.PERFECT_MODE_OFF,
        new RecordArgs('')
      )
    });
  }
  public onPerfectModeOff(record: MessageRecord): void {
    if (this._whiteboard.tools.drawTools[record.ownerId]) {
      this._whiteboard.tools.drawTools[record.ownerId].perfect = false;
    }

    if (this._whiteboard.focuses[record.ownerId]) {
      this._whiteboard.focuses[record.ownerId].boundingBox.perfect = false;
    }
  }

  public sendElementsCreated(parameters: any): WebSocket.MessageSendStatus {
    const serializedElement = parameters.element.toJSON();
    serializedElement[this.elementStringTypeKey] = ElementType[serializedElement.type];
    if (serializedElement.content) {
      serializedElement.content = encodeURIComponent(serializedElement.content);
    }

    const data = JSON.stringify({
      trigger: 'default',
      elements: [serializedElement]
    });
    return this.send(this.publicBroadcast, {
      store: true,
      broadcast: true,
      record: this.buildRecord(
        WhiteboardEvents.ELEMENTS_CREATED,
        new RecordArgs(data)
      )
    });
  }
  public onElementsCreated(record: MessageRecord): void {
    const recordData = JSON.parse(record.data);
    const elements = recordData.elements;

    /* remove all existing elements */
    elements.forEach((elementJSON: any) => {
      for (const element of this._whiteboard.container.elements) {
        if (elementJSON.ownerId === element.ownerId && elementJSON.index === element.index) {
          this._whiteboard.container.remove(element, true, false);
        }
      }
    });
    elements.forEach((elementJSON: any) => {
      const element = this._whiteboard.container.createElementFromJSON(elementJSON);
      this._whiteboard.container.add(element);

      if (this.localUser.accessType === AccessType.STUDENT) {
        element.selectable = element.ownerId === this.localUser.id + '';
      }
    });
  }

  public onAddProblem(record: MessageRecord): void {
    const recordData: any = JSON.parse(record.data);

    if (recordData.asImage) {
      WhiteboardService.assetURLtoAssetImages(recordData.assetURL).then(this.addProblem.bind(this));
    } else {
      WhiteboardService.assetURLtoAssetHTML(recordData.assetURL).then(this.addProblem.bind(this));
    }
  }

  public sendPostProblem(parameters: any): WebSocket.MessageSendStatus {
    const data = JSON.stringify(parameters);
    return this.send(this.teacherBroadcast, {
      store: true,
      broadcast: true,
      selfProcess: false,
      record: this.buildRecord(
        WhiteboardEvents.POST_PROBLEM,
        new RecordArgs(data)
      )
    });
  }
  public onPostProblem(record: MessageRecord): void {
    const recordData = JSON.parse(record.data);
    this.postProblem(
      recordData.rect,
      decodeURIComponent(recordData.content),
      recordData.elementOwnerId,
      recordData.elementIndex
    );
  }

  public sendElementsFocused(parameters: any): WebSocket.MessageSendStatus {
    /* get ids from focused elements */
    const ids: {ownerId: string; index: number}[] = Array.from(parameters.elements).map((element: ElementView) => { return {ownerId: element.ownerId, index: element.index}; });
    if (ids.length === 0) {
      return;
    }

    const data = JSON.stringify({ids});
    return this.send(this.publicBroadcast, {
      store: false,
      broadcast: true,
      record: this.buildRecord(
        WhiteboardEvents.ELEMENTS_FOCUSED,
        new RecordArgs(data)
      )
    });
  }
  public onElementsFocused(record: MessageRecord): void {
    const recordData = JSON.parse(record.data);
    recordData.ids.forEach((id: {ownerId: string; index: number}) => {
      const element = this._whiteboard.container.getElementById(id.ownerId, id.index);
      this._whiteboard.container.blur(
        element,
        false /* don't call callback function */
      ); /* blur element from whiteboard */
      const modeEditableElement = this._whiteboard.container.tools.editNodeTool.editableElement as unknown as ElementView;

      if (
        modeEditableElement === element ||
        this._whiteboard.container.tools.editTableTool.editableElement === element
      ) {
        this._whiteboard.container.tools.selectTool.on(); /* turn on select tool when someone selects currently editing element */
      }
    });
  }

  public sendDragMouseDown(parameters: any): WebSocket.MessageSendStatus {
    if (!this._whiteboard.streaming) {
      return;
    }
    const ids: {ownerId: string; index: number}[] = Array.from(parameters.elements).map((element: ElementView) => { return {ownerId: element.ownerId, index: element.index}; });
    if (ids.length === 0) {
      return;
    }

    const data = JSON.stringify({
      ids,
      position: parameters.position
    });
    return this.send(this.publicBroadcast, {
      store: false,
      broadcast: true,
      record: this.buildRecord(
        WhiteboardEvents.DRAG_MOUSE_DOWN,
        new RecordArgs(data)
      )
    });
  }
  public onDragMouseDown(record: MessageRecord): void {
    const recordData = JSON.parse(record.data);

    if (!this._whiteboard.tools.selectTools[record.ownerId]) {
      this._whiteboard.tools.selectTools[record.ownerId] = new SelectTool(this._whiteboard.container, new Focus(this._whiteboard.container));
      recordData.ids.forEach((id: {ownerId: string; index: number}) => {
        const element = this._whiteboard.container.getElementById(id.ownerId, id.index);
        this._whiteboard.tools.selectTools[record.ownerId].focus.appendChild(element, false, false);
        this._whiteboard.container.blur(element);
      });
    }
    this._whiteboard.tools.selectTools[record.ownerId].dragTool.makeMouseDown(recordData.position, false);
  }

  public sendDragMouseMove(parameters: any): WebSocket.MessageSendStatus {
    if (!this._whiteboard.streaming) {
      return;
    }
    const data = JSON.stringify({
      position: parameters.position
    });
    return this.send(this.publicBroadcast, {
      store: false,
      broadcast: true,
      record: this.buildRecord(
        WhiteboardEvents.DRAG_MOUSE_MOVE,
        new RecordArgs(data)
      )
    });
  }
  public onDragMouseMove(record: MessageRecord): void {
    const recordData = JSON.parse(record.data);
    this._whiteboard.tools.selectTools[record.ownerId]?.dragTool.makeMouseMove(recordData.position, false);
  }

  public sendDragMouseUp(parameters: any): WebSocket.MessageSendStatus {
    if (!this._whiteboard.streaming) {
      return;
    }
    const data = JSON.stringify({
      position: parameters.position
    });
    return this.send(this.publicBroadcast, {
      store: false,
      broadcast: true,
      record: this.buildRecord(
        WhiteboardEvents.DRAG_MOUSE_UP,
        new RecordArgs(data)
      )
    });
  }
  public onDragMouseUp(record: MessageRecord): void {
    const recordData = JSON.parse(record.data);
    this._whiteboard.tools.selectTools[record.ownerId]?.dragTool.makeMouseUp(recordData.position, false);
  }

  public sendElementsDragged(parameters: any): WebSocket.MessageSendStatus {
    if (parameters.delta.x === 0 && parameters.delta.y === 0) {
      return;
    }

    const ids: {ownerId: string; index: number}[] = Array.from(parameters.elements)
      .map((element: ElementView) => { return {ownerId: element.ownerId, index: element.index}; });

    if (ids.length === 0) {
      return;
    }

    const data: string = JSON.stringify({
      ids,
      angle: parameters.angle,
      refPoint: parameters.refPoint,
      position: parameters.position,
      delta: parameters.delta,
    });

    /* This is a temporary solution. Setting position for multiple elements is broken.
    * for that we use drag instead of setting position. And when streaming is on, this will prevent redundant dragging
    *  */
    return this.send(this.publicBroadcast, {
      store: true,
      broadcast: true,
      record: this.buildRecord(
        WhiteboardEvents.ELEMENTS_DRAGGED,
        new RecordArgs(data)
      )
    });
  }
  public onElementsDragged(record: MessageRecord): void {
    if (this._whiteboard.tools.selectTools[record.ownerId]) {
      this._whiteboard.tools.selectTools[record.ownerId] = undefined;
      return;
    }

    this._whiteboard.tools.selectTools[record.ownerId] = undefined;
    const recordData = JSON.parse(record.data);
    const focus: Focus = new Focus(this._whiteboard.container);

    recordData.ids.forEach((id: {ownerId: string; index: number}) => {
      const element: ElementView = this._whiteboard.container.getElementById(id.ownerId, id.index);
      focus.appendChild(element, false, false);
      this._whiteboard.container.blur(element);
    });

    focus.drag(recordData.delta, false);
  }



  public sendNodeEditMouseClick(parameters: any): WebSocket.MessageSendStatus {
    const data = JSON.stringify({
      position: parameters.position,
      order: parameters.order,
      elementIndex: parameters.element.index,
      elementOwnerId: parameters.element.ownerId
    });
    return this.send(this.teacherBroadcast, {
      store: true,
      broadcast: true,
      record: this.buildRecord(
        WhiteboardEvents.NODE_EDIT_MOUSE_CLICK,
        new RecordArgs(data)
      )
    });
  }
  public onNodeEditMouseClick(record: MessageRecord): void {
    const recordData = JSON.parse(record.data);
    if (!this._whiteboard.tools.editSegmentTools[record.ownerId]) {
      this._whiteboard.tools.editSegmentTools[record.ownerId] = new EditSegmentTool(this._whiteboard.container, new Focus(this._whiteboard.container));
      const targetElement = this._whiteboard.container.getElementById(recordData.elementOwnerId, recordData.elementIndex, true);

      if (targetElement instanceof SegmentView) {
        this._whiteboard.tools.editSegmentTools[record.ownerId].editableElement = targetElement;
      } else {
        return; /* if target element is not segment, do nothing. Only segment element's nodes can be edited */
      }
    }

    /* blur currently editable element, if someone starts editing that element */
    const segmentElement = this._whiteboard.container.tools.editSegmentTool.editableElement as unknown as ElementView;
    const remoteSegmentElement = this._whiteboard.tools.editSegmentTools[record.ownerId].editableElement as unknown as ElementView;
    if (
      segmentElement &&
      segmentElement.id === remoteSegmentElement.id
    ) {
      this._whiteboard.container.tools.editNodeTool.removeEditableElement();
    }
    this._whiteboard.tools.editSegmentTools[record.ownerId].makeMouseClick(recordData.position, false, recordData.order);

    this._whiteboard.tools.editSegmentTools[record.ownerId] = undefined;
  }

  public sendNodeEditMouseDown(parameters: any): WebSocket.MessageSendStatus {
    if (!this._whiteboard.streaming) {
      return;
    }

    const data = JSON.stringify({
      position: parameters.position,
      order: parameters.order,
      elementIndex: parameters.element.index,
      elementOwnerId: parameters.element.ownerId
    });
    return this.send(this.teacherBroadcast, {
      store: false,
      broadcast: true,
      record: this.buildRecord(
        WhiteboardEvents.NODE_EDIT_MOUSE_DOWN,
        new RecordArgs(data)
      )
    });
  }
  public onNodeEditMouseDown(record: MessageRecord): void {
    const recordData = JSON.parse(record.data);
    if (!this._whiteboard.tools.editNodeTools[record.ownerId]) {
      this._whiteboard.tools.editNodeTools[record.ownerId] = new EditNodeTool(this._whiteboard.container, new Focus(this._whiteboard.container));
      this._whiteboard.tools.editNodeTools[record.ownerId].showNodes = false;
      const targetElement = this._whiteboard.container.getElementById(recordData.elementOwnerId, recordData.elementIndex, true);

      if (targetElement instanceof PointedView || targetElement instanceof GraphicView) {
        this._whiteboard.tools.editNodeTools[record.ownerId].editableElement = targetElement;
      } else {
        return; /* if target element is not pointed, do nothing. Only pointed element's nodes can be edited */
      }
    }

    /* blur currently editable element, if someone starts editing that element */
    const modeEditableElement = this._whiteboard.container.tools.editNodeTool.editableElement as unknown as ElementView;
    const remoteNodeEditableElement = this._whiteboard.tools.editNodeTools[record.ownerId].editableElement as unknown as ElementView;
    if (
      modeEditableElement &&
      modeEditableElement.id === remoteNodeEditableElement.id
    ) {
      this._whiteboard.container.tools.editNodeTool.removeEditableElement();
    }
    this._whiteboard.tools.editNodeTools[record.ownerId].makeMouseDown(recordData.position, false, recordData.order);
  }

  public sendNodeEditMouseMove(parameters: any): WebSocket.MessageSendStatus {
    if (!this._whiteboard.streaming) {
      return;
    }

    const data = JSON.stringify({
      position: parameters.position,
      order: parameters.order,
      elementIndex: parameters.element.index,
      elementOwnerId: parameters.element.ownerId
    });
    return this.send(this.teacherBroadcast, {
      store: false,
      broadcast: true,
      record: this.buildRecord(
        WhiteboardEvents.NODE_EDIT_MOUSE_MOVE,
        new RecordArgs(data)
      )
    });
  }
  public onNodeEditMouseMove(record: MessageRecord): void {
    const recordData = JSON.parse(record.data);
    this._whiteboard.tools.editNodeTools[record.ownerId]?.makeMouseMove(recordData.position, false, recordData.order);
  }

  public sendNodeEditMouseUp(parameters: any): WebSocket.MessageSendStatus {
    if (!this._whiteboard.streaming) {
      return;
    }

    const data = JSON.stringify({
      position: parameters.position,
      order: parameters.order,
      elementIndex: parameters.element.index,
      elementOwnerId: parameters.element.ownerId
    });
    return this.send(this.teacherBroadcast, {
      store: false,
      broadcast: true,
      record: this.buildRecord(
        WhiteboardEvents.NODE_EDIT_MOUSE_UP,
        new RecordArgs(data)
      )
    });
  }
  public onNodeEditMouseUp(record: MessageRecord): void {
    const recordData = JSON.parse(record.data);
    this._whiteboard.tools.editNodeTools[record.ownerId]?.makeMouseUp(recordData.position, false, recordData.order);
    this._whiteboard.tools.editNodeTools[record.ownerId] = undefined;
  }

  public sendNodeEdited(parameters: any): WebSocket.MessageSendStatus {
    const data = JSON.stringify({
      position: parameters.position,
      order: parameters.order,
      elementIndex: parameters.element.index,
      elementOwnerId: parameters.element.ownerId
    });
    return this.send(this.teacherBroadcast, {
      store: true,
      broadcast: true,
      record: this.buildRecord(
        WhiteboardEvents.NODE_EDITED,
        new RecordArgs(data)
      )
    });
  }
  public onNodeEdited(record: MessageRecord): void {
    const recordData = JSON.parse(record.data);
    const element = this._whiteboard.container.getElementById(recordData.elementOwnerId, recordData.elementIndex, true);
    if (element instanceof PointedView || element instanceof GraphicView) {
      element.replacePoint(recordData.order, recordData.position);
    }
  }



  public sendTableEditMouseDown(parameters: any): WebSocket.MessageSendStatus {
    if (!this._whiteboard.streaming) {
      return;
    }

    const data = JSON.stringify({
      type: parameters.type,
      position: parameters.position,
      order: parameters.order,
      elementIndex: parameters.element.index,
      elementOwnerId: parameters.element.ownerId
    });
    return this.send(this.teacherBroadcast, {
      store: false,
      broadcast: true,
      record: this.buildRecord(
        WhiteboardEvents.TABLE_EDIT_MOUSE_DOWN,
        new RecordArgs(data)
      )
    });
  }
  public onTableEditMouseDown(record: MessageRecord): void {
    const recordData = JSON.parse(record.data);
    if (!this._whiteboard.tools.editTableTools[record.ownerId]) {
      this._whiteboard.tools.editTableTools[record.ownerId] = new EditTableTool(this._whiteboard.container, new Focus(this._whiteboard.container));
      const targetElement = this._whiteboard.container.getElementById(recordData.elementOwnerId, recordData.elementIndex, true);

      if (targetElement instanceof TableView) {
        this._whiteboard.tools.editTableTools[record.ownerId].showGrips = false;
        this._whiteboard.tools.editTableTools[record.ownerId].editableElement = targetElement;
      } else {
        return; /* if target element is not Table, do nothing */
      }
    }

    /* blur currently editable element, if someone starts editing that element */
    if (this._whiteboard.container.tools.editTableTool.editableElement && this._whiteboard.container.tools.editTableTool.editableElement.id === this._whiteboard.tools.editTableTools[record.ownerId].editableElement.id) {
      this._whiteboard.container.tools.editTableTool.removeEditableElement();
    }

    this._whiteboard.tools.editTableTools[record.ownerId].makeMouseDown(recordData.position, false, {order: recordData.order, type: recordData.type});
  }

  public sendTableEditMouseMove(parameters: any): WebSocket.MessageSendStatus {
    if (!this._whiteboard.streaming) {
      return;
    }

    const data = JSON.stringify({
      type: parameters.type,
      position: parameters.position,
      order: parameters.order,
      elementIndex: parameters.element.index,
      elementOwnerId: parameters.element.ownerId
    });
    return this.send(this.teacherBroadcast, {
      store: false,
      broadcast: true,
      record: this.buildRecord(
        WhiteboardEvents.TABLE_EDIT_MOUSE_MOVE,
        new RecordArgs(data)
      )
    });
  }
  public onTableEditMouseMove(record: MessageRecord): void {
    const recordData = JSON.parse(record.data);
    this._whiteboard.tools.editTableTools[record.ownerId]?.makeMouseMove(recordData.position, false, {order: recordData.order, type: recordData.type});
  }

  public sendTableEditMouseUp(parameters: any): WebSocket.MessageSendStatus {
    if (!this._whiteboard.streaming) {
      return;
    }

    const data = JSON.stringify({
      type: parameters.type,
      position: parameters.position,
      order: parameters.order,
      elementIndex: parameters.element.index,
      elementOwnerId: parameters.element.ownerId
    });
    return this.send(this.teacherBroadcast, {
      store: false,
      broadcast: true,
      record: this.buildRecord(
        WhiteboardEvents.TABLE_EDIT_MOUSE_UP,
        new RecordArgs(data)
      )
    });
  }
  public onTableEditMouseUp(record: MessageRecord): void {
    const recordData = JSON.parse(record.data);
    this._whiteboard.tools.editTableTools[record.ownerId]?.makeMouseUp(recordData.position, false, {order: recordData.order, type: recordData.type});
  }

  public sendTableEdited(parameters: any): WebSocket.MessageSendStatus {
    const data = JSON.stringify({
      type: parameters.type,
      position: parameters.position,
      order: parameters.order,
      size: parameters.size,
      elementIndex: parameters.element.index,
      elementOwnerId: parameters.element.ownerId
    });
    return this.send(this.teacherBroadcast, {
      store: true,
      broadcast: true,
      record: this.buildRecord(
        WhiteboardEvents.TABLE_EDITED,
        new RecordArgs(data)
      )
    });
  }
  public onTableEdited(record: MessageRecord): void {
    const recordData = JSON.parse(record.data);
    const element = this._whiteboard.container.getElementById(recordData.elementOwnerId, recordData.elementIndex, true);
    if (element instanceof TableView) {
      switch (recordData.type) {
        case Table.ROW:
          element.modifyRow(recordData.order, recordData.size);
          break;
        case Table.COL:
          element.modifyCol(recordData.order, recordData.size);
          break;
      }
    }
  }

  public sendTableRecreated(parameters: any): WebSocket.MessageSendStatus {
    const data = JSON.stringify({
      rect: parameters.element.getRect(),
      rows: parameters.rows,
      cols: parameters.cols,
      elementIndex: parameters.element.index,
      elementOwnerId: parameters.element.ownerId
    });
    return this.send(this.teacherBroadcast, {
      store: true,
      broadcast: true,
      record: this.buildRecord(
        WhiteboardEvents.TABLE_RECREATED,
        new RecordArgs(data)
      )
    });
  }
  public onTableRecreated(record: MessageRecord): void {
    const recordData = JSON.parse(record.data);
    const element = this._whiteboard.container.getElementById(recordData.elementOwnerId, recordData.elementIndex, true);
    if (element instanceof TableView) {
      element.recreate(recordData.rect, recordData.rows, recordData.cols, false);
    }
  }

  public sendSetTable(parameters: any): WebSocket.MessageSendStatus {
    const data = JSON.stringify({
      rows: parameters.rows,
      cols: parameters.cols,
      elementIndex: parameters.element.index,
      elementOwnerId: parameters.element.ownerId
    });
    return this.send(this.teacherBroadcast, {
      store: true,
      broadcast: true,
      record: this.buildRecord(
        WhiteboardEvents.SET_TABLE,
        new RecordArgs(data)
      )
    });
  }
  public onSetTable(record: MessageRecord): void {
    const recordData = JSON.parse(record.data);
    const element = this._whiteboard.container.getElementById(recordData.elementOwnerId, recordData.elementIndex, true);
    if (element instanceof TableView) {
      element.setTable(recordData.rect, recordData.rows, recordData.cols, false);
    }
  }

  public sendAddTableRow(parameters: any): WebSocket.MessageSendStatus {
    const data = JSON.stringify({
      height: parameters.height,
      after: parameters.after,
      elementIndex: parameters.element.index,
      elementOwnerId: parameters.element.ownerId
    });
    return this.send(this.teacherBroadcast, {
      store: true,
      broadcast: true,
      record: this.buildRecord(
        WhiteboardEvents.ADD_TABLE_ROW,
        new RecordArgs(data)
      )
    });
  }
  public onAddTableRow(record: MessageRecord): void {
    const recordData = JSON.parse(record.data);
    const element = this._whiteboard.container.getElementById(recordData.elementOwnerId, recordData.elementIndex, true);
    if (element instanceof TableView) {
      element.addRow(recordData.height, recordData.after, false);
    }
  }

  public sendAddTableCol(parameters: any): WebSocket.MessageSendStatus {
    const data = JSON.stringify({
      width: parameters.width,
      after: parameters.after,
      elementIndex: parameters.element.index,
      elementOwnerId: parameters.element.ownerId
    });
    return this.send(this.teacherBroadcast, {
      store: true,
      broadcast: true,
      record: this.buildRecord(
        WhiteboardEvents.ADD_TABLE_COL,
        new RecordArgs(data)
      )
    });
  }
  public onAddTableCol(record: MessageRecord): void {
    const recordData = JSON.parse(record.data);
    const element = this._whiteboard.container.getElementById(recordData.elementOwnerId, recordData.elementIndex, true);
    if (element instanceof TableView) {
      element.addCol(recordData.width, recordData.after, false);
    }
  }

  public sendRemoveTableRow(parameters: any): WebSocket.MessageSendStatus {
    const data = JSON.stringify({
      index: parameters.index,
      elementIndex: parameters.element.index,
      elementOwnerId: parameters.element.ownerId
    });
    return this.send(this.teacherBroadcast, {
      store: true,
      broadcast: true,
      record: this.buildRecord(
        WhiteboardEvents.REMOVE_TABLE_ROW,
        new RecordArgs(data)
      )
    });
  }
  public onRemoveTableRow(record: MessageRecord): void {
    const recordData = JSON.parse(record.data);
    const element = this._whiteboard.container.getElementById(recordData.elementOwnerId, recordData.elementIndex, true);
    if (element instanceof TableView) {
      element.removeRow(recordData.index, false);
    }
  }

  public sendRemoveTableCol(parameters: any): WebSocket.MessageSendStatus {
    const data = JSON.stringify({
      index: parameters.index,
      elementIndex: parameters.element.index,
      elementOwnerId: parameters.element.ownerId
    });
    return this.send(this.teacherBroadcast, {
      store: true,
      broadcast: true,
      record: this.buildRecord(
        WhiteboardEvents.REMOVE_TABLE_COL,
        new RecordArgs(data)
      )
    });
  }
  public onRemoveTableCol(record: MessageRecord): void {
    const recordData = JSON.parse(record.data);
    const element = this._whiteboard.container.getElementById(recordData.elementOwnerId, recordData.elementIndex, true);
    if (element instanceof TableView) {
      element.removeCol(recordData.index, false);
    }
  }



  public sendResizeMouseDown(parameters: any): WebSocket.MessageSendStatus {
    if (!this._whiteboard.streaming) {
      return;
    }
    const ids: {ownerId: string; index: number}[] = Array.from(parameters.elements).map((element: ElementView) => { return {ownerId: element.ownerId, index: element.index}; });
    if (ids.length === 0) {
      return;
    }

    const data = JSON.stringify({
      ids,
      position: parameters.position,
      compass: parameters.compass
    });
    return this.send(this.publicBroadcast, {
      store: false,
      broadcast: true,
      record: this.buildRecord(
        WhiteboardEvents.RESIZE_MOUSE_DOWN,
        new RecordArgs(data)
      )
    });
  }
  public onResizeMouseDown(record: MessageRecord): void {
    const recordData = JSON.parse(record.data);
    if (!this._whiteboard.focuses[record.ownerId]) {
      this._whiteboard.focuses[record.ownerId] = new Focus(this._whiteboard.container);

      recordData.ids.forEach((id: {ownerId: string; index: number}) => {
        const element = this._whiteboard.container.getElementById(id.ownerId, id.index);
        this._whiteboard.focuses[record.ownerId].appendChild(element, false, false);
      });
    }
    this._whiteboard.focuses[record.ownerId].boundingBox.makeResizeMouseDown(recordData.position, recordData.compass, false);
  }

  public sendResizeMouseMove(parameters: any): WebSocket.MessageSendStatus {
    if (!this._whiteboard.streaming) {
      return;
    }

    const data = JSON.stringify({
      position: parameters.position,
      compass: parameters.compass
    });
    return this.send(this.publicBroadcast, {
      store: false,
      broadcast: true,
      record: this.buildRecord(
        WhiteboardEvents.RESIZE_MOUSE_MOVE,
        new RecordArgs(data)
      )
    });
  }
  public onResizeMouseMove(record: MessageRecord): void {
    const recordData = JSON.parse(record.data);
    this._whiteboard.focuses[record.ownerId]?.boundingBox.makeResizeMouseMove(recordData.position, recordData.compass, false);
  }

  public sendResizeMouseUp(parameters: any): WebSocket.MessageSendStatus {
    if (!this._whiteboard.streaming) {
      return;
    }

    const data = JSON.stringify({
      position: parameters.position,
      compass: parameters.compass
    });
    return this.send(this.publicBroadcast, {
      store: false,
      broadcast: true,
      record: this.buildRecord(
        WhiteboardEvents.RESIZE_MOUSE_UP,
        new RecordArgs(data)
      )
    });
  }
  public onResizeMouseUp(record: MessageRecord): void {
    const recordData = JSON.parse(record.data);
    this._whiteboard.focuses[record.ownerId]?.boundingBox.makeResizeMouseUp(recordData.position, recordData.compass, false);
    this._whiteboard.focuses[record.ownerId] = undefined;
  }

  public sendElementsResized(parameters: any): WebSocket.MessageSendStatus {
    const ids: {ownerId: string; index: number}[] = Array.from(parameters.elements).map((element: ElementView) => { return {ownerId: element.ownerId, index: element.index}; });
    if (ids.length === 0) {
      return;
    }

    const data = JSON.stringify({
      ids,
      rect: parameters.rect,
      refPoint: parameters.refPoint,
    });
    return this.send(this.publicBroadcast, {
      store: true,
      broadcast: true,
      record: this.buildRecord(
        WhiteboardEvents.ELEMENTS_RESIZED,
        new RecordArgs(data)
      )
    });
  }
  public onElementsResized(record: MessageRecord): void {
    const recordData = JSON.parse(record.data);
    const focus = new Focus(this._whiteboard.container);
    recordData.ids.forEach((id: {ownerId: string; index: number}) => {
      const element = this._whiteboard.container.getElementById(id.ownerId, id.index);
      focus.appendChild(element, false, false);
      this._whiteboard.container.blur(element);
    });
    focus.refPoint = recordData.refPoint;
    focus.setRect(recordData.rect);
  }



  public sendRefPointChanged(parameters: any): WebSocket.MessageSendStatus {
    const ids: {ownerId: string; index: number}[] = Array.from(parameters.elements).map((element: ElementView) => { return {ownerId: element.ownerId, index: element.index}; });
    if (ids.length === 0) {
      return;
    }

    const data = JSON.stringify({
      ids,
      newRefPoint: parameters.newRefPoint,
      oldRefPoint: parameters.oldRefPoint
    });
    return this.send(this.publicBroadcast, {
      store: true,
      broadcast: true,
      record: this.buildRecord(
        WhiteboardEvents.REF_POINT_CHANGED,
        new RecordArgs(data)
      )
    });
  }
  public onRefPointChanged(record: MessageRecord): void {
    const recordData = JSON.parse(record.data);
    const focus = new Focus(this._whiteboard.container);
    recordData.ids.forEach((id: {ownerId: string; index: number}) => {
      const element = this._whiteboard.container.getElementById(id.ownerId, id.index);
      if (!element) { return; }
      focus.appendChild(element, false, false);
    });
    focus.refPoint = recordData.newRefPoint;
  }

  public sendRecenterRefPoint(parameters: any): WebSocket.MessageSendStatus {
    const ids: {ownerId: string; index: number}[] = Array.from(parameters.elements).map((element: ElementView) => { return {ownerId: element.ownerId, index: element.index}; });
    if (ids.length === 0) {
      return;
    }

    const data = JSON.stringify({
      ids,
      center: parameters.center
    });
    return this.send(this.publicBroadcast, {
      store: true,
      broadcast: true,
      record: this.buildRecord(
        WhiteboardEvents.RECENTER_REF_POINT,
        new RecordArgs(data, undefined)
      )
    });
  }
  public onRecenterRefPoint(record: MessageRecord): void {
    const recordData = JSON.parse(record.data);
    const focus = new Focus(this._whiteboard.container);
    recordData.ids.forEach((id: {ownerId: string; index: number}) => {
      const element = this._whiteboard.container.getElementById(id.ownerId, id.index);
      if (!element) { return; }
      focus.appendChild(element, false, false);
    });
    focus.refPoint = recordData.center;
  }



  public sendRotatePointMouseDown(parameters: any): WebSocket.MessageSendStatus {
    if (!this._whiteboard.streaming) {
      return;
    }

    const ids: {ownerId: string; index: number}[] = Array.from(parameters.elements).map((element: ElementView) => { return {ownerId: element.ownerId, index: element.index}; });
    if (ids.length === 0) {
      return;
    }

    const data = JSON.stringify({
      ids,
      position: parameters.position,
      refPoint: parameters.refPoint,
    });
    return this.send(this.publicBroadcast, {
      store: false,
      broadcast: true,
      record: this.buildRecord(
        WhiteboardEvents.ROTATE_MOUSE_DOWN,
        new RecordArgs(data)
      )
    });
  }
  public onRotateMouseDown(record: MessageRecord): void {
    const recordData = JSON.parse(record.data);
    if (!this._whiteboard.focuses[record.ownerId]) {
      this._whiteboard.focuses[record.ownerId] = new Focus(this._whiteboard.container);
      recordData.ids.forEach((id: {ownerId: string; index: number}) => {
        const element = this._whiteboard.container.getElementById(id.ownerId, id.index);
        this._whiteboard.focuses[record.ownerId].appendChild(element, false, false);
      });
      this._whiteboard.focuses[record.ownerId].refPoint = recordData.refPoint;
    }
    this._whiteboard.focuses[record.ownerId].boundingBox.makeRotateMouseDown(recordData.position, false);
  }

  public sendRotatePointMouseMove(parameters: any): WebSocket.MessageSendStatus {
    if (!this._whiteboard.streaming) {
      return;
    }

    const data = JSON.stringify({
      position: parameters.position
    });
    return this.send(this.publicBroadcast, {
      store: false,
      broadcast: true,
      record: this.buildRecord(
        WhiteboardEvents.ROTATE_MOUSE_MOVE,
        new RecordArgs(data)
      )
    });
  }
  public onRotateMouseMove(record: MessageRecord): void {
    const recordData = JSON.parse(record.data);
    this._whiteboard.focuses[record.ownerId]?.boundingBox.makeRotateMouseMove(recordData.position, false);
  }

  public sendRotatePointMouseUp(parameters: any): WebSocket.MessageSendStatus {
    if (!this._whiteboard.streaming) {
      return;
    }

    const data = JSON.stringify({
      position: parameters.position
    });
    return this.send(this.publicBroadcast, {
      store: false,
      broadcast: true,
      record: this.buildRecord(
        WhiteboardEvents.ROTATE_MOUSE_UP,
        new RecordArgs(data)
      )
    });
  }
  public onRotateMouseUp(record: MessageRecord): void {
    const recordData = JSON.parse(record.data);
    this._whiteboard.focuses[record.ownerId]?.boundingBox.makeRotateMouseUp(recordData.position, false);
  }

  public sendElementsRotated(parameters: any): WebSocket.MessageSendStatus {
    const ids: {ownerId: string; index: number}[] = Array.from(parameters.elements).map((element: ElementView) => { return {ownerId: element.ownerId, index: element.index}; });
    if (ids.length === 0) {
      return;
    }

    const data = JSON.stringify({
      ids,
      newAngle: parameters.newAngle,
      oldAngle: parameters.oldAngle,
      refPoint: parameters.refPoint
    });
    return this.send(this.publicBroadcast, {
      store: true,
      broadcast: true,
      record: this.buildRecord(
        WhiteboardEvents.ELEMENTS_ROTATED,
        new RecordArgs(data)
      )
    });
  }
  public onElementsRotated(record: MessageRecord): void {
    if (this._whiteboard.focuses[record.ownerId]) {
      this._whiteboard.focuses[record.ownerId] = undefined;
      return;
    }

    this._whiteboard.focuses[record.ownerId] = undefined;

    const recordData = JSON.parse(record.data);
    const focus = new Focus(this._whiteboard.container);

    recordData.ids.forEach((id: {ownerId: string; index: number}) => {
      const element = this._whiteboard.container.getElementById(id.ownerId, id.index);
      focus.appendChild(element, false, false);
    });
    focus.refPoint = recordData.refPoint;
    focus.rotate(recordData.newAngle, recordData.oldAngle);
  }



  public sendHighlightMouseDown(parameters: any): WebSocket.MessageSendStatus {
    if (!this._whiteboard.streaming) {
      return;
    }

    const data = JSON.stringify({
      position: parameters.position,
      settings: parameters.settings
    });
    return this.send(this.teacherBroadcast, {
      store: false,
      broadcast: true,
      record: this.buildRecord(
        WhiteboardEvents.HIGHLIGHT_MOUSE_DOWN,
        new RecordArgs(data)
      )
    });
  }
  public onHighlightMouseDown(record: MessageRecord): void {
    const recordData = JSON.parse(record.data);
    if (!this._whiteboard.tools.highlighterTools[record.ownerId]) {
      this._whiteboard.tools.highlighterTools[record.ownerId] = new HighlightTool(this._whiteboard.container);
    }

    this._whiteboard.tools.highlighterTools[record.ownerId].color = recordData.settings.color;
    this._whiteboard.tools.highlighterTools[record.ownerId].width = recordData.settings.width;
    this._whiteboard.tools.highlighterTools[record.ownerId].timeout = recordData.settings.timeout;
    this._whiteboard.tools.highlighterTools[record.ownerId].makeMouseDown(recordData.position, false, recordData.settings);
  }

  public sendHighlightMouseMove(parameters: any): WebSocket.MessageSendStatus {
    if (!this._whiteboard.streaming) {
      return;
    }

    const data = JSON.stringify({
      position: parameters.position,
      path: parameters.element.path.toString()
    });
    return this.send(this.teacherBroadcast, {
      store: false,
      broadcast: true,
      record: this.buildRecord(
        WhiteboardEvents.HIGHLIGHT_MOUSE_MOVE,
        new RecordArgs(data)
      )
    });
  }
  public onHighlightMouseMove(record: MessageRecord): void {
    const recordData = JSON.parse(record.data);
    this._whiteboard.tools.highlighterTools[record.ownerId].makeMouseMove(recordData.position, false, recordData.path);
  }

  public sendHighlightMouseUp(parameters: any): WebSocket.MessageSendStatus {
    if (!this._whiteboard.streaming) {
      return;
    }

    const data = JSON.stringify({
      position: parameters.position,
      path: parameters.element.path.toString()
    });
    return this.send(this.teacherBroadcast, {
      store: false,
      broadcast: true,
      record: this.buildRecord(
        WhiteboardEvents.HIGHLIGHT_MOUSE_UP,
        new RecordArgs(data)
      )
    });
  }
  public onHighlightMouseUp(record: MessageRecord): void {
    const recordData = JSON.parse(record.data);
    this._whiteboard.tools.highlighterTools[record.ownerId].makeMouseUp(recordData.position, false, recordData.path);
    this._whiteboard.tools.highlighterTools[record.ownerId] = undefined;
  }

  public sendHighlighted(parameters: any): WebSocket.MessageSendStatus {
    const data = JSON.stringify({
      settings: parameters.settings,
      path: parameters.element.path.toString()
    });
    return this.send(this.teacherBroadcast, {
      store: false,
      broadcast: !this._whiteboard.streaming,
      record: this.buildRecord(
        WhiteboardEvents.HIGHLIGHTED,
        new RecordArgs(data)
      )
    });
  }
  public onHighlighted(record: MessageRecord): void {
    if (this._whiteboard.streaming) {
      return;
    }
    const recordData = JSON.parse(record.data);
    this._whiteboard.container.tools.highlightTool.color = recordData.settings.color;
    this._whiteboard.container.tools.highlightTool.width = recordData.settings.width;
    this._whiteboard.container.tools.highlightTool.timeout = recordData.settings.timeout;

    this._whiteboard.container.tools.highlightTool.highlight(recordData.path);
  }



  public sendPointerToolOn(): WebSocket.MessageSendStatus {
    const recordData: string = JSON.stringify([this.localUser.id]);
    return this.send(this.addDestination, {
      store: false,
      broadcast: true,
      record: this.buildRecord(
        WhiteboardEvents.POINTER_TOOL,
        new RecordArgs(recordData)
      )
    });
  }
  public onPointerToolOn(record: MessageRecord): void {
    const remoteUsersIds: number[] = JSON.parse(record.data);
    remoteUsersIds.forEach((id: number) => {
      const remoteUser: RemoteUserAdditionalInfoStateModel = this.remoteUsers[id];
      if (!remoteUser) {
        return;
      }

      if (remoteUser.accessType === AccessType.STUDENT) {
        if (!this._whiteboard.tools.studentPointerTools[id]) {
          this._whiteboard.tools.studentPointerTools[id] = this.createStudentPointer(remoteUser.personalColor);
          this._whiteboard.tools.studentPointerTools[id].add();
        }

        WhiteboardService.hidePointer(this._whiteboard.tools.studentPointerTools[id]);
      } else {
        this.incrementStaffPointerCount();
        this.showStaffPointer();
      }
    });
  }

  public sendPointerMouseMove(parameters: any): WebSocket.MessageSendStatus {
    const data = JSON.stringify({
      position: parameters.position
    });
    return this.send(this.publicBroadcast, {
      store: false,
      broadcast: true,
      record: this.buildRecord(
        WhiteboardEvents.POINTER_MOUSE_MOVE,
        new RecordArgs(data)
      )
    });
  }
  public onPointerMouseMove(record: MessageRecord): void {
    const recordData = JSON.parse(record.data);
    const user: RemoteUserAdditionalInfoStateModel = this.remoteUsers[record.ownerId];
    if (user.accessType === AccessType.STUDENT) {
      this._whiteboard.tools.studentPointerTools[record.ownerId]?.makeMouseMove(recordData.position, false);
    } else {
      this._whiteboard.staffPointerTool.makeMouseMove(recordData.position, false);
    }
  }

  public sendPointerToolOff(): WebSocket.MessageSendStatus {
    const recordData: string = JSON.stringify([this.localUser.id]);
    return this.send(this.subDestination, {
      store: false,
      broadcast: true,
      mirrorState: WhiteboardEvents.POINTER_TOOL,
      record: this.buildRecord(
        WhiteboardEvents.POINTER_TOOL_OFF,
        new RecordArgs(recordData)
      )
    });
  }
  public onPointerToolOff(record: MessageRecord): void {
    const remoteUsersIds: number[] = JSON.parse(record.data);
    remoteUsersIds.forEach((id: number) => {
      const remoteUser: RemoteUserAdditionalInfoStateModel = this.remoteUsers[id];
      if (remoteUser.accessType === AccessType.STUDENT) {
        this._whiteboard.tools.studentPointerTools[id]?.remove();
        this._whiteboard.tools.studentPointerTools[id] = undefined;
      } else {
        this.decrementStaffPointerCount();
      }
    });
  }

  public sendStyleChange(parameters: any): WebSocket.MessageSendStatus {
    if (!this._whiteboard.streaming) {
      return;
    }
    const ids: {ownerId: string; index: number}[] = Array.from(parameters.elements).map((element: ElementView) => { return {ownerId: element.ownerId, index: element.index}; });
    if (ids.length === 0) {
      return;
    }

    const data = JSON.stringify({
      ids,
      style: parameters.style,
    });
    return this.send(this.teacherBroadcast, {
      store: false,
      broadcast: true,
      record: this.buildRecord(
        WhiteboardEvents.STYLE_CHANGE,
        new RecordArgs(data)
      )
    });
  }
  public sendStyleChangeCommit(parameters: any): WebSocket.MessageSendStatus {
    const ids: {ownerId: string; index: number}[] = Array.from(parameters.elements).map((element: ElementView) => { return {ownerId: element.ownerId, index: element.index}; });

    const data = JSON.stringify({
      ids,
      style: parameters.style,
    });
    return this.send(this.teacherBroadcast, {
      store: ids.length !== 0, /* send anyway, but store if not empty */
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        WhiteboardEvents.STYLE_CHANGE,
        new RecordArgs(data)
      )
    });
  }
  public onStyleChange(record: MessageRecord): void {
    const recordData = JSON.parse(record.data);
    recordData.ids.forEach((id: {ownerId: string; index: number}) => {
      const element = this._whiteboard.container.getElementById(id.ownerId, id.index);
      element.style.set = recordData.style;
    });
  }

  public sendTextTyping(parameters: any): WebSocket.MessageSendStatus {
    if (!this._whiteboard.streaming) {
      return;
    }

    const data = JSON.stringify({
      elementIndex: parameters.element.index,
      elementOwnerId: parameters.element.ownerId,
      text: parameters.newText
    });
    return this.send(this.publicBroadcast, {
      store: false,
      broadcast: true,
      record: this.buildRecord(
        WhiteboardEvents.TEXT_TYPING,
        new RecordArgs(data)
      )
    });
  }
  public sendTextTypingCommit(parameters: any): WebSocket.MessageSendStatus {
    const data = JSON.stringify({
      elementIndex: parameters.element.index,
      elementOwnerId: parameters.element.ownerId,
      text: parameters.newText
    });
    return this.send(this.publicBroadcast, {
      store: true,
      broadcast: true,
      record: this.buildRecord(
        WhiteboardEvents.TEXT_TYPING,
        new RecordArgs(data)
      )
    });
  }
  public onTextTyping(record: MessageRecord): void {
    const recordData = JSON.parse(record.data);
    const element = this._whiteboard.container.getElementById(recordData.elementOwnerId, recordData.elementIndex);
    if (element instanceof TextBoxView) {
      element.text = recordData.text;
    }
  }

  public sendNumberLineEdit(parameters: any): WebSocket.MessageSendStatus {
    const data = JSON.stringify({
      elementIndex: parameters.element.index,
      elementOwnerId: parameters.element.ownerId,
      xAxis: parameters.element.xAxisModel,
      numberSize: parameters.element.numberSize,
      showNumbers: parameters.element.numbersVisible,
      showBorder: parameters.element.borderVisible
    });
    return this.send(this.teacherBroadcast, {
      store: true,
      broadcast: true,
      selfProcess: false,
      record: this.buildRecord(
        WhiteboardEvents.NUMBER_LINE_EDITED,
        new RecordArgs(data)
      )
    });
  }
  public onNumberLineEdit(record: MessageRecord): void {
    const recordData = JSON.parse(record.data);
    const element = this._whiteboard.container.getElementById(recordData.elementOwnerId, recordData.elementIndex);
    if (element instanceof NumberLineView2) {
      element.edit(recordData.xAxis, recordData.numberSize, recordData.showNumbers, recordData.showBorder, false);
    }
  }

  public sendCoordinatePlaneEdit(parameters: any): WebSocket.MessageSendStatus {
    const data = JSON.stringify({
      elementIndex: parameters.element.index,
      elementOwnerId: parameters.element.ownerId,
      xAxis: parameters.element.xAxisModel,
      yAxis: parameters.element.yAxisModel,
      grid: parameters.element.gridModel,
      numberSize: parameters.element.numberSize,
      showNumbers: parameters.element.numbersVisible,
      showBorder: parameters.element.borderVisible
    });
    return this.send(this.teacherBroadcast, {
      store: true,
      broadcast: true,
      selfProcess: false,
      record: this.buildRecord(
        WhiteboardEvents.COORDINATE_PLANE_EDITED,
        new RecordArgs(data)
      )
    });
  }
  public onCoordinatePlaneEdit(record: MessageRecord): void {
    const recordData = JSON.parse(record.data);
    const element = this._whiteboard.container.getElementById(recordData.elementOwnerId, recordData.elementIndex);
    if (element instanceof CoordinatePlaneView2) {
      element.edit(recordData.xAxis, recordData.yAxis, recordData.grid, recordData.numberSize, recordData.showNumbers, recordData.showBorder, false);
    }
  }

  public sendAssetEdit(parameters: any): WebSocket.MessageSendStatus {
    if (!this._whiteboard.streaming) {
      return;
    }

    const data = JSON.stringify({
      elementIndex: parameters.element.index,
      elementOwnerId: parameters.element.ownerId,
      content: parameters.element.content.innerHTML
    });
    return this.send(this.teacherBroadcast, {
      store: false,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        WhiteboardEvents.ASSET_EDITING,
        new RecordArgs(data)
      )
    });
  }
  public sendAssetEditCommit(parameters: any): WebSocket.MessageSendStatus {
    const data = JSON.stringify({
      elementIndex: parameters.element.index,
      elementOwnerId: parameters.element.ownerId,
      content: parameters.element.content.innerHTML
    });
    return this.send(this.teacherBroadcast, {
      store: true,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        WhiteboardEvents.ASSET_EDITING,
        new RecordArgs(data)
      )
    });
  }
  public onAssetEdit(record: MessageRecord): void {
    if (this._whiteboard.streaming) {
      return;
    }
    const recordData = JSON.parse(record.data);
    const element = this._whiteboard.container.getElementById(recordData.elementOwnerId, recordData.elementIndex);
    if (element instanceof ForeignObjectView) {
      element.setContent(recordData.content);
    }
  }

  public sendElementsDeleted(parameters: any): WebSocket.MessageSendStatus {
    const ids: {ownerId: string; index: number}[] = Array.from(parameters.elements).map((element: ElementView) => { return {ownerId: element.ownerId, index: element.index}; });
    if (ids.length === 0) {
      return;
    }

    const data = JSON.stringify({
      trigger: 'default',
      ids
    });
    return this.send(this.publicBroadcast, {
      store: true,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        WhiteboardEvents.ELEMENTS_DELETED,
        new RecordArgs(data)
      )
    });
  }
  public onElementsDeleted(record: MessageRecord): void {
    const recordData = JSON.parse(record.data);
    recordData.ids.forEach((id: {ownerId: string; index: number}) => {
      const element = this._whiteboard.container.getElementById(id.ownerId, id.index);
      if (element) {
        this._whiteboard.container.remove(element, true, false);
      }
    });
  }



  public sendToTop(parameters: any): WebSocket.MessageSendStatus {
    const ids: {ownerId: string; index: number}[] = Array.from(parameters.elements).map((element: ElementView) => { return {ownerId: element.ownerId, index: element.index}; });
    if (ids.length === 0) {
      return;
    }

    const data = JSON.stringify({
      ids
    });
    return this.send(this.teacherBroadcast, {
      store: true,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        WhiteboardEvents.TO_TOP,
        new RecordArgs(data)
      )
    });
  }
  public onToTop(record: MessageRecord): void {
    const recordData = JSON.parse(record.data);
    const focus = new Focus(this._whiteboard.container);
    recordData.ids.forEach((id: {ownerId: string; index: number}) => {
      const element = this._whiteboard.container.getElementById(id.ownerId, id.index);
      focus.appendChild(element, false, false);
    });
    focus.orderTop(false);
  }

  public sendToBottom(parameters: any): WebSocket.MessageSendStatus {
    const ids: {ownerId: string; index: number}[] = Array.from(parameters.elements).map((element: ElementView) => { return {ownerId: element.ownerId, index: element.index}; });
    if (ids.length === 0) {
      return;
    }

    const data = JSON.stringify({
      ids
    });
    return this.send(this.teacherBroadcast, {
      store: true,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        WhiteboardEvents.TO_BOTTOM,
        new RecordArgs(data)
      )
    });
  }
  public onToBottom(record: MessageRecord): void {
    const recordData = JSON.parse(record.data);
    const focus = new Focus(this._whiteboard.container);
    recordData.ids.forEach((id: {ownerId: string; index: number}) => {
      const element = this._whiteboard.container.getElementById(id.ownerId, id.index);
      focus.appendChild(element, false, false);
    });
    focus.orderBottom(false);
  }



  public sendGroup(parameters: any): WebSocket.MessageSendStatus {
    const ids: {ownerId: string; index: number}[] = Array.from(parameters.elements).map((element: ElementView) => { return {ownerId: element.ownerId, index: element.index}; });
    if (ids.length === 0) {
      return;
    }

    const data = JSON.stringify({
      ids,
      groupOwnerId: parameters.group.ownerId,
      groupIndex: parameters.group.index,
    });
    return this.send(this.teacherBroadcast, {
      store: true,
      broadcast: true,
      selfProcess: false,
      record: this.buildRecord(
        WhiteboardEvents.GROUP,
        new RecordArgs(data)
      )
    });
  }
  public onGroup(record: MessageRecord): void {
    const recordData = JSON.parse(record.data);
    const focus = new Focus(this._whiteboard.container);
    recordData.ids.forEach((id: {ownerId: string; index: number}) => {
      const element = this._whiteboard.container.getElementById(id.ownerId, id.index);
      focus.appendChild(element, false, false);
    });
    const createdGroup: GroupView = focus.group(false, false);
    createdGroup.setId(recordData.groupOwnerId, recordData.groupIndex);

    if (this.localUser.accessType === AccessType.STUDENT) {
      createdGroup.selectable = createdGroup.ownerId === this.localUser.id + '';
    }
  }

  public sendUngroup(parameters: any): WebSocket.MessageSendStatus {
    const data = JSON.stringify({
      elementOwnerId: parameters.element.ownerId,
      elementIndex: parameters.element.index
    });
    return this.send(this.teacherBroadcast, {
      store: true,
      broadcast: true,
      record: this.buildRecord(
        WhiteboardEvents.UNGROUP,
        new RecordArgs(data)
      )
    });
  }
  public onUngroup(record: MessageRecord): void {
    const recordData = JSON.parse(record.data);
    const focus = new Focus(this._whiteboard.container);
    const element = this._whiteboard.container.getElementById(recordData.elementOwnerId, recordData.elementIndex);
    focus.appendChild(element, false, false);
    focus.ungroup(false);
  }



  public sendCopy(parameters: any): WebSocket.MessageSendStatus {
    const ids: {ownerId: string; index: number}[] = Array.from(parameters.elements).map((element: ElementView) => { return {ownerId: element.ownerId, index: element.index}; });
    if (ids.length === 0) {
      return;
    }

    const data = JSON.stringify({
      ids
    });
    return this.send(this.publicBroadcast, {
      /* sending this event only for log */
      store: false,
      broadcast: false,
      selfProcess: true,
      record: this.buildRecord(
        WhiteboardEvents.LOCAL_COPY,
        new RecordArgs(data)
      )
    });
  }
  /* sendCopy sends LOCAL_COPY event only for logging. */

  public sendCut(parameters: any): WebSocket.MessageSendStatus {
    const ids: {ownerId: string; index: number}[] = Array.from(parameters.elements).map((element: ElementView) => { return {ownerId: element.ownerId, index: element.index}; });
    if (ids.length === 0) {
      return;
    }

    const data = JSON.stringify({
      ids,
      trigger: 'cut'
    });
    return this.send(this.publicBroadcast, {
      store: true,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        WhiteboardEvents.ELEMENTS_DELETED,
        new RecordArgs(data)
      )
    });
  }
  /* sendCut sends ELEMENTS_DELETED event */

  public sendPaste(parameters: any): WebSocket.MessageSendStatus {
    const elements = Array.from(parameters.elements).map((element: ElementView) => {
      const elementJSON = element.toJSON();
      elementJSON[this.elementStringTypeKey] = ElementType[elementJSON.type];
      return elementJSON;
    });

    const errorMessages: { [key: string]: string} = {};
    errorMessages[WebSocket.MessageSendStatus.LIMIT_EXCEEDED] = 'Clipboard size exceeds max limit. Please try again with fewer elements or contact support';

    const data = JSON.stringify({
      trigger: 'paste',
      elements
    });
    return this.send(this.publicBroadcast, {
      store: true,
      broadcast: true,
      record: this.buildRecord(
        WhiteboardEvents.ELEMENTS_CREATED,
        new RecordArgs(data)
      )
    }, errorMessages);
  }
  /* sendPaste sends ELEMENTS_CREATED event */



  public sendReset(): WebSocket.MessageSendStatus {
    this.sendUnshare();
    this.sendPurge();
    return this.send(this.purgeDestination, {
      store: false,
      broadcast: true,
      record: this.buildRecord(
        WhiteboardEvents.RESET,
        new RecordArgs('')
      )
    });
  }
  public onReset() {
    if (this.localUser.accessType === AccessType.STUDENT || this.localUser.accessType === AccessType.OBSERVER) {
      this.reset(false, true);
      this._whiteboard.container.style.strokeColor = this.localUser.personalColor;
      this._whiteboard.container.style.fontColor = this.localUser.personalColor;
    } else {
      this.reset(false);
    }
  }


  /* state handling */
  public sendGridOn(): WebSocket.MessageSendStatus {
    return this.send(this.teacherBroadcast, {
      store: true,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        WhiteboardStates.GRID,
        new RecordArgs(StateValue.ON)
      )
    });
  }
  public sendGridOff(): WebSocket.MessageSendStatus {
    return this.send(this.teacherBroadcast, {
      store: true,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        WhiteboardStates.GRID,
        new RecordArgs(StateValue.OFF)
      )
    });
  }
  public onGrid(record: MessageRecord): void {
    if (record.data === StateValue.ON) {
      this._whiteboard.container.grid.gridOn();
    } else {
      this._whiteboard.container.grid.gridOff();
      this._whiteboard.container.grid.snapOff();
    }
  }

  public sendSnapOn(): WebSocket.MessageSendStatus {
    return this.send(this.teacherBroadcast, {
      store: true,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        WhiteboardStates.SNAP,
        new RecordArgs(StateValue.ON)
      )
    });
  }
  public sendSnapOff(): WebSocket.MessageSendStatus {
    return this.send(this.teacherBroadcast, {
      store: true,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        WhiteboardStates.SNAP,
        new RecordArgs(StateValue.OFF)
      )
    });
  }
  public onSnap(record: MessageRecord): void {
    if (record.data === StateValue.ON) {
      this._whiteboard.container.grid.gridOn();
      this._whiteboard.container.grid.snapOn();
    } else {
      this._whiteboard.container.grid.snapOff();
    }
  }

  public sendSnapSideChange(parameters: any): WebSocket.MessageSendStatus {
    return this.send(this.teacherBroadcast, {
      store: true,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        WhiteboardStates.SNAP_SIDE,
        new RecordArgs(parameters.snapSide)
      )
    });
  }
  public onSnapSideChange(record: MessageRecord): void {
    const isSnap: number = record.data as unknown as number;
    this._whiteboard.container.grid.setSnapSide(isSnap, false);
  }


  public sendHeight(height: number): WebSocket.MessageSendStatus {
    return this.send(this.teacherBroadcast, {
      store: true,
      broadcast: true,
      selfProcess: false,
      record: this.buildRecord(
        WhiteboardStates.HEIGHT,
        new RecordArgs(height + '')
      )
    });
  }
  public onHeight(record: MessageRecord): void {
    this.extend(false, parseFloat(record.data));
  }

  public sendScroll(position: number): WebSocket.MessageSendStatus {
    return this.send(this.teacherBroadcast, {
      store: true,
      broadcast: true,
      record: this.buildRecord(
        WhiteboardStates.SCROLL,
        new RecordArgs(position + '')
      )
    });
  }
  public onScroll(record: MessageRecord): void {
    this.scroll(parseFloat(record.data), false);
  }

  public sendStreamingOn(): WebSocket.MessageSendStatus {
    return this.send(this.teacherBroadcast, {
      store: true,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        WhiteboardStates.STREAMING,
        new RecordArgs(StateValue.ON)
      )
    });
  }
  public sendStreamingOff(): WebSocket.MessageSendStatus {
    return this.send(this.teacherBroadcast, {
      store: true,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        WhiteboardStates.STREAMING,
        new RecordArgs(StateValue.OFF)
      )
    });
  }
  public onStreaming(record: MessageRecord): void {
    if (record.data === StateValue.ON) {
      this.setStreaming(true, false);
    } else {
      this.setStreaming(false, false);
    }
  }

  public sendSmoothOn(): WebSocket.MessageSendStatus {
    return this.send(this.teacherBroadcast, {
      store: true,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        WhiteboardStates.SMOOTH,
        new RecordArgs(StateValue.ON)
      )
    });
  }
  public sendSmoothOff(): WebSocket.MessageSendStatus {
    return this.send(this.teacherBroadcast, {
      store: true,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        WhiteboardStates.SMOOTH,
        new RecordArgs(StateValue.OFF)
      )
    });
  }
  public onSmooth(record: MessageRecord): void {
    if (record.data === StateValue.ON) {
      this.setSmooth(true, false);
    } else {
      this.setSmooth(false, false);
    }
  }

  public sendProblemResizeMode(problemId: string, resizeMode: ResizeMode): void {
    const recordData: string = JSON.stringify({problemId, resizeMode});
    this.send(this.teacherBroadcast, {
      store: true,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        WhiteboardEvents.CHANGE_PROBLEM_RESIZE_MODE,
        new RecordArgs(recordData)
      )
    });
  }
  public onProblemResizeMode(record: MessageRecord): void {
    const recordData: {problemId: string; resizeMode: ResizeMode} = JSON.parse(record.data);

    const id = ElementView.parseId(recordData.problemId);
    const problem: ForeignObjectView = this._whiteboard.container.getElementById(id.ownerId, id.index, true) as ForeignObjectView;

    this.setResizeMode(problem, recordData.resizeMode, false);
  }

  public sendProblemStretchModeOn(problemId: string): void {
    const recordData: string = JSON.stringify(problemId);
    this.send(this.teacherBroadcast, {
      store: true,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        WhiteboardEvents.CHANGE_PROBLEM_RESIZE_MODE,
        new RecordArgs(recordData)
      )
    });
  }

  public shareWithAll(): void {
    this.dispatchShareWithAll();
    this.afterShare();
  }
  public shareWith(userId: number): void {
    this.dispatchShareWith(userId);
    this.afterShareWith(userId);
  }
  public unshareWithAll(): void {
    this.dispatchUnshareWithAll();
    this.afterUnshare();
  }
  public unshareWith(userId: number): void {
    this.dispatchUnshareWith(userId);
    this.afterUnshareWith(userId);
  }

  private static preprocessProblemStyles(element: HTMLElement): void {
    ProblemPreprocessor.lim(element);
    ProblemPreprocessor.fraction(element);
    ProblemPreprocessor.nLogarithm(element);
    ProblemPreprocessor.logarithm(element);
  }

  private static preprocessProblemImages(parentNode: HTMLElement): void {
    parentNode.childNodes.forEach((childNode: ChildNode): void => {
      if (childNode instanceof HTMLImageElement) {
        let sourceAttr = childNode.getAttribute('src');

        if (sourceAttr.includes('resource_svg')) {
          sourceAttr += ((/\?/).test(sourceAttr) ? '&' : '?') + 'name=img.svg';

          sourceAttr = sourceAttr.replace('/cms/resource_svg', 'resource_svg');
        }

        sourceAttr = sourceAttr.replace(/^http:\/\//, 'https://');

        childNode.setAttribute('src', sourceAttr);
      } else {
        WhiteboardService.preprocessProblemImages(childNode as HTMLElement);
      }
    });
  }

  private static preprocessBeforePost(parentNode: HTMLElement): void {
    const div: HTMLDivElement = document.createElement('div');
    div.id = 'tmp_problem_styling';
    div.style.opacity = '0';
    div.style.position = 'absolute';

    div.appendChild(parentNode);

    document.body.appendChild(div);

    WhiteboardService.preprocessProblemStyles(parentNode);

    document.body.removeChild(div);

    WhiteboardService.preprocessProblemImages(parentNode);
  };

  /**
   * @param contentType - MIME image types.
   * By default - image/png
   * Possible values - image/png, image/jpeg, image/webp ...
   *
   * @returns promise of image data as blob
   * */
  public exportSnapshot(contentType?: string): Promise<Blob> {
    const imageElement: HTMLImageElement = document.createElement('img');

    const whiteboardClone: HTMLElement = this._whiteboard.container.HTML.cloneNode(true) as HTMLElement;

    const whiteboardWrapper: HTMLElement = document.createElement('div');
    whiteboardWrapper.appendChild(whiteboardClone);
    whiteboardWrapper.style.width = this._whiteboard.width + 'px';
    whiteboardWrapper.style.height = this._whiteboard.height + 'px';
    whiteboardWrapper.style.position = 'absolute';
    whiteboardWrapper.style.transform = `translate(-${this._whiteboard.width + 10}px)`;

    document.body.appendChild(whiteboardWrapper);

    return new Promise((resolve, reject): void => {
      domtoimage
        .toSvg(whiteboardClone, {cacheBust: true})
        .then((dataURL: string): void => {
          /* make the generated svg valid */
          dataURL = dataURL.replace('xmlns="http://www.w3.org/1999/xhtml"', '');

          imageElement.onload = (): void => {
            /* draw the image onto the canvas */
            const canvasElement = document.createElement('canvas');

            canvasElement.width = this._whiteboard.width;
            canvasElement.height = this._whiteboard.height;

            canvasElement.getContext('2d').getImageData(0, 0, this._whiteboard.width, this._whiteboard.height);

            canvasElement.getContext('2d').drawImage(imageElement, 0, 0);
            canvasElement.toBlob((blob: Blob): void => {
              document.body.removeChild(whiteboardWrapper);
              resolve(blob);
            }, contentType);
          };

          imageElement.onerror = reject;

          imageElement.src = dataURL;
        })
        .catch(reject);
    });
  }

  private getUploadURL(classId: number, lessonNumber: number, contentType: string): Observable<{ s3Key: string; s3Url: S3Url }> {
    return this.http.get(
      `${this.classroom.vcrURL}/whiteboard/snapshot/upload-url`,
      {
        params: {
          classId,
          lessonNumber,
          contentType
        }
      }
    ).pipe(map((response: any) => {
      return response.data as { s3Key: string; s3Url: S3Url };
    }));
  }

  public saveSnapshot(classId: number, lessonNumber: number, snapshot: Blob): Promise<{ s3Key: string; s3Url: S3Url }> {
    return new Promise<{ s3Key: string; s3Url: S3Url }>((resolve, reject): void => {
      this.getUploadURL(classId, lessonNumber, snapshot.type)
      .pipe(catchError((err: any): ObservableInput<void> => {
        reject(err);
        return;
      }))
      .subscribe((result: { s3Key: string; s3Url: S3Url }): void => {
        if (!result?.s3Url || !result?.s3Url?.contentType) {
          reject();
          return;
        }

        const headers: HttpHeaders = new HttpHeaders();
        headers.append('Content-Type', result.s3Url.contentType);
        const options = {
          headers,
          reportProgress: true
        };

        const request: HttpRequest<Blob> = new HttpRequest('PUT', result.s3Url.url, snapshot, options);

        /* upload image */
        this.http.request(request)
          .pipe(catchError((err: any): ObservableInput<void> => {
            reject(err);
            return;
          }))
          .subscribe((event: HttpEvent<any>): void => {
            /* wait for image to fully upload */
            if (event instanceof HttpResponse) {
              resolve(result);
            }
          });

      });
    });
  }

  public exportAndSaveSnapshot(classId: number, lessonNumber: number): Promise<void> {
    return new Promise((resolve, reject): void => {
      this.exportSnapshot()
        .then((snapshot: Blob): void => {
          this.saveSnapshot(classId, lessonNumber, snapshot)
            .then((result: { s3Key: string; s3Url: S3Url }): void => {
              this.notifyAfterSnapshotSave(result.s3Key)
                  .subscribe(resolve);
            })
            .catch(reject);
        })
        .catch(reject);
    });
  }

  private notifyAfterSnapshotSave(s3Key: string): Observable<any> {
    return this.http.get(
      `${this.classroom.vcrURL}/whiteboard/snapshot/saved`, {
        params: { s3Key }
      }
    );
  }

  public static hidePointer(pointerTool: PointerTool): void {
    pointerTool?.setPosition({x: -50, y: -50});
  }

  public copyObject(object: any): any {
    return JSON.parse(JSON.stringify(object));
  }

  /* MESSAGING DISPATCHER FUNCTION */
  public send(destination: string, message: ClassroomMessage, messages?: {[key: string]: string}): WebSocket.MessageSendStatus {
    const sendStatus: WebSocket.MessageSendStatus = this.webSocketService.send(destination, JSON.stringify(message));
    if (sendStatus === WebSocket.MessageSendStatus.LIMIT_EXCEEDED) {
      this.redraw();
    }
    if (sendStatus !== WebSocket.MessageSendStatus.SUCCESSFUL) {
      this.onMessageSendError.emit({status: sendStatus, messages});
    }

    return sendStatus;
  }

  /**
   * set element selectable if local user isn't student
   * or if local user is student, and element is created by that user
   */
  protected setElementSelectable(element: ElementView): void {
    if (!element) {
      return;
    }
    if (this.localUser.accessType === AccessType.STUDENT) {
      /* if local user is student, set true if element owner is local user */
      element.selectable = element.ownerId === this.localUser.id + '';
    } else {
      /* if local user isn't student, then all elements should be selectable */
      element.selectable = true;
    }
  }

  protected getEditableElementId(): { ownerId: string; index: number } {
    let editableElementId: {ownerId: string; index: number};

    if (this._whiteboard.container.tools.editNodeTool.isOn()) {
      const editableElement = this._whiteboard.container.tools.editNodeTool.editableElement as unknown as ElementView;
      editableElementId = {
        ownerId: editableElement.ownerId,
        index: editableElement.index
      };
    } else if (this._whiteboard.container.tools.editTableTool.isOn()) {
      editableElementId = {
        ownerId: this._whiteboard.container.tools.editTableTool.editableElement.ownerId,
        index: this._whiteboard.container.tools.editTableTool.editableElement.index
      };
    }

    return editableElementId;
  }
  protected setEditableElement(editableElementId: { ownerId: string; index: number }): void {
    const element = this._whiteboard.container.getElementById(editableElementId.ownerId, editableElementId.index, true);
    if (this._whiteboard.container.tools.editNodeTool.isOn()) {
      if (element) {
        this._whiteboard.container.tools.editNodeTool.editableElement = element as PointedView;
      } else {
        this._whiteboard.container.tools.drawTool.setDrawer(this._whiteboard.container.drawers.free);
        this._whiteboard.container.tools.drawTool.on();
      }
    } else if (this._whiteboard.container.tools.editTableTool.isOn()) {
      if (element) {
        this._whiteboard.container.tools.editTableTool.editableElement = element as TableView;
      } else {
        this._whiteboard.container.tools.drawTool.setDrawer(this._whiteboard.container.drawers.free);
        this._whiteboard.container.tools.drawTool.on();
      }
    }
  }

  protected static normalizeProblem(problemStr: string): string {
    const cleanDiv: HTMLDivElement = document.createElement('div');
    cleanDiv.innerHTML = problemStr.replace(/\r?\n/, '\n');

    WhiteboardService.cleanProblem(cleanDiv);
    return cleanDiv.innerHTML;
  }
  protected static cleanProblem(problem: HTMLElement): void {
    const children: NodeListOf<ChildNode> = problem.childNodes;
    children.forEach((childNode: ChildNode) => {
      const childElement = childNode as HTMLElement;

      if (
        childElement.className === 'answerHidden' ||
        childElement.className === 'notesHidden' ||
        childElement.tagName === 'EXAMPLE'
      ) {
        problem.removeChild(childElement);
      } else if (childElement.hasChildNodes()) {
        WhiteboardService.cleanProblem(childElement);
      }
    });
  }

  public static assetURLsToAssetImages(assetURLs: AssetURL[]): Promise<AssetHTML[]> {
    const assetHTMLPromises: Promise<AssetHTML>[] = assetURLs.map((assetURL: AssetURL): Promise<AssetHTML> =>
      WhiteboardService.assetURLtoAssetImages(assetURL)
    );

    return Promise.all(assetHTMLPromises);
  }
  public static assetURLtoAssetImages(assetURL: AssetURL): Promise<AssetHTML> {
    if (assetURL.problem.positionAsLetter) {
      assetURL.problem.positionAsLetter += '.';
    }

    return new Promise<AssetHTML>((resolve, reject): void => {
      WhiteboardService.problemURLToImage(assetURL.problem)
        .then((problem: ProblemHTML): void => {
          const subProblemsPromises: Promise<ProblemHTML>[] = assetURL.subProblems?.map((subProblem: ProblemURL) => {
            if (subProblem.positionAsLetter) {
              subProblem.positionAsLetter += ')';
            }

            return WhiteboardService.problemURLToImage(subProblem);
          });

          Promise.all(subProblemsPromises)
            .then((subProblems: ProblemHTML[]): void => {
              resolve({problem, subProblems});
            })
            .catch(reject);
        })
        .catch(reject);
    });
  }

  public static assetURLsToAssetHTMLs(assetURLs: AssetURL[]): Promise<AssetHTML[]> {
    const assetHTMLPromises: Promise<AssetHTML>[] = assetURLs.map((assetURL: AssetURL): Promise<AssetHTML> =>
      WhiteboardService.assetURLtoAssetHTML(assetURL)
    );

    return Promise.all(assetHTMLPromises);
  }
  public static assetURLtoAssetHTML(assetURL: AssetURL): Promise<AssetHTML> {
    if (assetURL.problem.positionAsLetter) {
      assetURL.problem.positionAsLetter += '.';
    }

    return new Promise<AssetHTML>((resolve, reject): void => {
      WhiteboardService.problemURLToHTML(assetURL.problem)
        .then((problem: ProblemHTML): void => {
          const subProblemsPromises: Promise<ProblemHTML>[] = assetURL.subProblems?.map((subProblem: ProblemURL) => {
            if (subProblem.positionAsLetter) {
              subProblem.positionAsLetter += ')';
            }

            return WhiteboardService.problemURLToHTML(subProblem);
          });

          Promise.all(subProblemsPromises)
            .then((subProblems: ProblemHTML[]): void => {
              resolve({problem, subProblems});
            })
            .catch(reject);
        })
        .catch(reject);
    });
  }

  private static problemURLToImage(problem: ProblemURL): Promise<ProblemHTML> {
    const problemDiv: HTMLDivElement = document.createElement('div');
    problemDiv.id = problem.id + '';
    problemDiv.style.lineHeight = '1.15';

    const problemImage: HTMLImageElement = document.createElement('img');
    problemImage.src = problem.URL;

    let problemPositionAsLetterDiv: HTMLDivElement | undefined;
    if (problem.positionAsLetter) {
      problemPositionAsLetterDiv = document.createElement('div');
      problemPositionAsLetterDiv.innerHTML = problem.positionAsLetter;
    }

    return new Promise<ProblemHTML>((resolve, reject): void => {
      if (problemPositionAsLetterDiv) {
        WhiteboardService.calculateBoundingClientRect(problemPositionAsLetterDiv, 'visible')
          .then((rect: Rect): void => {
            problemDiv.appendChild(problemPositionAsLetterDiv);
            problemDiv.appendChild(problemImage);

            resolve({
              id: problem.id,
              HTML: problemDiv,
              height: problem.height + rect.height,
              width: problem.width + rect.width
            });
          })
          .catch(reject);
      } else {
        problemDiv.appendChild(problemImage);

        resolve({
          id: problem.id,
          HTML: problemDiv,
          height: problem.height,
          width: problem.width
        });
      }
    });
  }
  private static problemURLToHTML(problem: ProblemURL): Promise<ProblemHTML> {
    const problemDiv: HTMLDivElement = document.createElement('div');
    problemDiv.id = problem.id + '';
    problemDiv.classList.add('problem-content');
    problemDiv.style.lineHeight = '1.15';

    return new Promise<ProblemHTML>((resolve, reject): void => {
      const request: XMLHttpRequest = new XMLHttpRequest();
      request.open('GET', problem.URL, true);

      request.onload = (): void => {
        if (request.readyState === 4) {
          if (request.status === 200) {
            const htmlDoc: Document = new DOMParser().parseFromString(request.responseText, 'text/html');
            const foreignObject: Element | null = htmlDoc.querySelector('svg foreignObject');

            if (!foreignObject) {
              reject('Asset ' + problem.id + ' has no SVG foreignObject to render');
              return;
            }

            problemDiv.innerHTML = foreignObject.innerHTML;
            /* before preprocessing: the math templates are sized against live layout,
               so the problem has to be at its real width by then */
            WhiteboardService.stretchProblemWrapper(problemDiv);
            WhiteboardService.preprocessBeforePost(problemDiv);

            WhiteboardService.calculateBoundingClientRect(problemDiv, 'visible')
              .then((rect: Rect): void => {
                resolve({
                  id: problem.id,
                  HTML: problemDiv,
                  height: rect.height,
                  width: rect.width + 5
                });
              })
              .catch(reject);
          } else {
            reject(request.statusText);
          }
        }
      };

      request.onerror = (): void => reject('Can\'t load asset content from ' + problem.URL);

      request.send(null);
    });
  }

  /**
   * How much of the problem column the content actually occupies.
   * The measuring box is given a definite width so that percentage-sized content resolves
   * against the column, which means the box itself is always the full column wide - the
   * right edge of the content is what tells us how wide the posted problem should be.
   * Text is measured through ranges rather than through its parent, since a paragraph
   * stretches the full column no matter how short the line inside it is.
   * */
  private static getContentWidth(div: HTMLDivElement, left: number): number {
    let right: number = left;

    const walker: TreeWalker = document.createTreeWalker(div, NodeFilter.SHOW_TEXT);
    const range: Range = document.createRange();

    for (let node: Node = walker.nextNode(); node; node = walker.nextNode()) {
      if (node.nodeValue && node.nodeValue.trim()) {
        range.selectNodeContents(node);
        right = Math.max(right, range.getBoundingClientRect().right);
      }
    }

    div.querySelectorAll('*').forEach((element: Element): void => {
      if (element.hasAttribute(WhiteboardService.STRETCHED_ATTRIBUTE)) {
        return;
      }

      if (!WhiteboardService.DERIVED_WIDTH_DISPLAYS.includes(getComputedStyle(element).display)) {
        right = Math.max(right, element.getBoundingClientRect().right);
      }
    });

    return right - left;
  }

  private static calculateBoundingClientRect(htmlElement: HTMLElement, overflow: 'visible' | 'hidden' = 'hidden', maxWidth?: number): Promise<Rect> {
    if (!maxWidth) {
      maxWidth = WhiteboardService.getWhiteboardMaxWidth();
    }

    const div: HTMLDivElement = document.createElement('div');
    div.id = 'tmp_problem_styling';
    div.style.opacity = '0';
    div.style.position = 'absolute';
    div.style.overflow = overflow;
    div.style.width = maxWidth + 'px';

    if (htmlElement instanceof HTMLElement) {
      div.innerHTML = htmlElement.outerHTML;
    } else {
      div.innerHTML = htmlElement;
    }

    return new Promise<Rect>((resolve, reject) => {
      WhiteboardService.areExternalResourcesLoaded(div)
        .then((): void => {
          const rect: Rect = div.getBoundingClientRect();

          rect.width = Math.min(WhiteboardService.getContentWidth(div, rect.x), maxWidth);

          if (overflow === 'visible') {
            rect.height = div.scrollHeight;
          }

          document.body.removeChild(div);

          rect.width += 20;
          rect.height += 20;
          resolve(rect);
        })
        .catch(reject);

      document.body.appendChild(div);
    });
  }

  protected static areExternalResourcesLoaded(divElement: HTMLDivElement): Promise<any[]> {
    const externalResources: NodeListOf<Element> = divElement.querySelectorAll('img[src], iframe[src], script[src]');

    const promises: Promise<any>[] = [];

    externalResources.forEach((resource: Element): void => {
      const src: string = resource.getAttribute('src');

      if (src) {
        promises.push(new Promise((resolve, reject): void => {
          const image: HTMLImageElement = new Image();

          image.onerror = reject;
          image.onload = resolve;

          image.src = src;
        }));
      }
    });

    return Promise.all(promises);
  }

  protected static getWhiteboardMaxWidth(): number {
    return (WhiteboardState.VISIBLE_WIDTH * 65) / 100;
  }

  public addProblem(asset: AssetHTML): void {
    const postPositionTop: number = this.calculatePostPosition();

    let lastPostedProblemSize: Size = {width: 0, height: 0};
    let postPositionBottom: number = postPositionTop;

    /* check if current problem header is the same as last problem header */
    if (this._whiteboard.lastPostedProblemId !== asset.problem.id) {
      postPositionBottom += WhiteboardService.PROBLEM_TOP_MARGIN;
      lastPostedProblemSize = {
        width: asset.problem.width,
        height: asset.problem.height
      };

      const rect = {x: WhiteboardService.PROBLEM_LEFT_MARGIN, y: postPositionBottom, width: lastPostedProblemSize.width, height: lastPostedProblemSize.height};
      const content = encodeURIComponent(asset.problem.HTML.outerHTML);
      const elementOwnerId = this.localUser.id + '';
      const elementIndex = this._whiteboard.container.nextElementIndex;

      this.postProblem(
        rect,
        decodeURIComponent(content),
        elementOwnerId,
        elementIndex
      );
      this.sendPostProblem({
        elementOwnerId,
        elementIndex,
        content,
        rect,
        isHeader: true,
        id: asset.problem.id
      });

      postPositionBottom += lastPostedProblemSize.height;
      this.dispatchLastPostedProblemId(asset.problem.id);
    }

    asset.subProblems?.forEach((subProblem: ProblemHTML): void => {
      postPositionBottom += WhiteboardService.SUB_PROBLEM_TOP_MARGIN;
      lastPostedProblemSize = {
        width: subProblem.width,
        height: subProblem.height
      };

      const rect = {x: WhiteboardService.PROBLEM_LEFT_MARGIN, y: postPositionBottom, width: lastPostedProblemSize.width, height: lastPostedProblemSize.height};
      const content = encodeURIComponent(subProblem.HTML.outerHTML);
      const elementOwnerId = this.localUser.id + '';
      const elementIndex = this._whiteboard.container.nextElementIndex;

      this.postProblem(
        rect,
        decodeURIComponent(content),
        elementOwnerId,
        elementIndex
      );
      this.sendPostProblem({
        elementOwnerId,
        elementIndex,
        content,
        rect,
        isHeader: false,
        id: subProblem.id
      });

      postPositionBottom += lastPostedProblemSize.height;
    });

    this.normalizeWBScroll(postPositionTop, postPositionBottom, lastPostedProblemSize.height);
  }

  /**
   * calculate and return the top position, where a problem can be posted.
   * */
  public calculatePostPosition(): number {
    let postPositionTop = [...this._whiteboard.container.elements]
      .map((element: ElementView): Rect => element.getRect())
      .map(({ y, height }: Rect): number => y + height)
      .reduce((a: number, b: number): number => Math.max(a, b), -Infinity);

    if (postPositionTop < this._whiteboard.scrollPosition) {
      postPositionTop = this._whiteboard.scrollPosition;
    }

    return postPositionTop;
  }

  /**
   * Normalize whiteboard scroll position and height.
   * Should be used after problem posting.
   * */
  public normalizeWBScroll(postPositionTop: number, postPositionBottom: number, lastPostedProblemHeight: number): void {
    if (postPositionBottom > this._whiteboard.height) {
      this.extend(true, postPositionBottom);
      this.scroll(postPositionTop, false);
    } else if (postPositionBottom > this._whiteboard.scrollPosition + WhiteboardState.VISIBLE_HEIGHT) {
      if (lastPostedProblemHeight > WhiteboardState.VISIBLE_HEIGHT) {
        this.scroll(postPositionTop, false);
      } else {
        this.scroll(postPositionBottom - WhiteboardState.VISIBLE_HEIGHT, false);
      }
    } else if (postPositionTop < this._whiteboard.scrollPosition) {
      this.scroll(postPositionTop, false);
    }
  }

  protected postProblem(rect: Rect, content: string, elementOwnerId?: string, elementIndex?: number): void {
    const problem: ForeignObjectView = new ForeignObjectView(
      this._whiteboard.container,
      {
        overEvent: true,
        globalStyle: false,
        contentEditable: true,
        proportionalResizable: true
      },
      rect,
      elementOwnerId,
      elementIndex
    );

    if (this.localUser.accessType === AccessType.STUDENT) {
      problem.selectable = parseInt(problem.ownerId) === this.localUser.id;
    }

    problem.SVG.setAttribute('data-class', 'problem');

    problem.refPoint = problem.center;
    problem.setContent(content);

    (problem.content.firstElementChild as HTMLDivElement).style.transformOrigin = '0px 0px';
    (problem.content.firstElementChild as HTMLDivElement).style.willChange = 'transform;';

    this._whiteboard.container.add(problem);
    problem.content.ondragstart = function() {return false;};
  }

  /**
   * @param foreignObject foreignObject element to set stretch mode
   * @param resizeMode ResizeMode enum
   * @param send send message to classroom members or not
   * */
  public setResizeMode(foreignObject: ForeignObjectView, resizeMode: ResizeMode, send: boolean = true): void {
    foreignObject.setResizeMode(resizeMode);

    foreignObject.proportionalResizable = resizeMode === ResizeMode.STRETCH;

    if (send) {
      this.sendProblemResizeMode(foreignObject.id, resizeMode);
    }
  }

  public buildRecord(command: string, args: RecordArgs): MessageRecord {
    const msgType = WhiteboardStates[command] ? MsgRecordType.STATE : WhiteboardEvents[command] ? MsgRecordType.EVENT : null;
    return {
      category: WhiteboardCommands.category,
      name: command,
      data: args.data,
      type: msgType,
      ownerId: null, /* must be set server-side on receive */
      targetId: args.targetId
    };
  }
}
